import React, { useState, useEffect, useCallback, useMemo } from 'react';
import BrokerConnectionManager from '@/components/BrokerConnectionManager';
import { SchwabAccountResponse, SchwabPosition, SchwabInstrument, SchwabSecuritiesAccount, SchwabAccountAPIItem } from '@/integrations/schwab/types';
import { Position as TradierPosition, UserProfile as TradierUserProfile, UserBalances as TradierUserBalances, TradierAccountProfile, TradierSourceData } from '@/integrations/tradier/types'; // Ensured TradierSourceData is imported
import { getTradierUserProfile, getTradierUserBalances, getTradierPositions } from '@/integrations/tradier/client';
import { TastytradeAccount, TastytradeAccountBalance, TastytradePosition, TastytradeSourceData } from '@/integrations/tastytrade/types'; // Added Tastytrade types
import tastytradeClient from '@/integrations/tastytrade/client'; // Added Tastytrade client
import { useBrokerConnections } from '@/hooks/use-broker-connections'; // Added import
import { formatCurrency } from '@/lib/utils';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"; // Added for collapsible section

// Helper function to fetch Schwab profile data
async function fetchBackendSchwabProfile(): Promise<{ accounts: SchwabAccountAPIItem[] }> {
  const response = await fetch('/api/schwab/profile');
  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`Failed to fetch Schwab profile: ${response.status} ${errorData}`);
  }
  return response.json();
}

// Interface for Tradier source data
interface TradierSourceData {
  profile: TradierUserProfile;
  balances: Record<string, TradierUserBalances>; // account_number -> balances
  positions: Record<string, TradierPosition[]>; // account_number -> positions
}

// --- Helper functions for calculations (parseOptionSymbol, EXPIRING_SOON_DAYS, calculateDTE, getOptionStatus, fetchQuotes) remain largely the same ---
// ... (Keep existing helper functions: parseOptionSymbol, EXPIRING_SOON_DAYS, calculateDTE, getOptionStatus, fetchQuotes) ...
// ... (Ensure fetchQuotes can handle an empty symbols array gracefully if needed) ...

interface ParsedOptionSymbolInfo {
  underlyingSymbol: string;
  expirationDate: Date; // UTC Date object
  putCall: 'PUT' | 'CALL';
  strikePrice: number;
}

const EXPIRING_SOON_DAYS = 8;

// Definition for AlpacaSDKQuote based on observed API response
interface AlpacaSDKQuote {
  ap: number; // Ask Price
  as: number; // Ask Size
  ax: string; // Ask Exchange
  bp: number; // Bid Price
  bs: number; // Bid Size
  bx: string; // Bid Exchange
  c: string[]; // Conditions
  t: string;  // Timestamp
  z?: string; // Tape
  // Include other fields if they appear in the actual API response and are needed
}

// Definition for the entire API response structure for quotes
interface AlpacaQuotesApiResponse {
  quotes: {
    [symbol: string]: AlpacaSDKQuote;
  };
}


function parseOptionSymbol(symbol: string | undefined): ParsedOptionSymbolInfo | null {
  if (!symbol) return null;
  const occSuffixMatch = symbol.match(/(\d{2})(\d{2})(\d{2})([PC])(\d{8})$/);
  if (!occSuffixMatch) return null;
  const matchedSuffixString = occSuffixMatch[0];
  const yearStr = occSuffixMatch[1];
  const monthStr = occSuffixMatch[2];
  const dayStr = occSuffixMatch[3];
  const typeChar = occSuffixMatch[4] as 'P' | 'C';
  const strikeChars = occSuffixMatch[5];
  const suffixStartIndex = symbol.lastIndexOf(matchedSuffixString);
  if (suffixStartIndex === -1) return null;
  const underlyingSymbol = symbol.substring(0, suffixStartIndex).trim();
  if (!underlyingSymbol) return null;
  const year = parseInt(yearStr, 10) + 2000;
  const month = parseInt(monthStr, 10) - 1;
  const day = parseInt(dayStr, 10);
  const expirationDate = new Date(Date.UTC(year, month, day));
  if (isNaN(expirationDate.getTime())) return null;
  const putCall = typeChar === 'P' ? 'PUT' : 'CALL';
  const strikePrice = parseInt(strikeChars, 10) / 1000;
  return { underlyingSymbol, expirationDate, putCall, strikePrice };
}

const calculateDTE = (expirationDateObj?: Date): number | string => {
  if (!expirationDateObj) return 'N/A';
  const today = new Date();
  const utcToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const utcExpiry = expirationDateObj.getTime();
  const diffTime = utcExpiry - utcToday;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : 0;
};

const getOptionStatus = (
  instrument: Pick<SchwabInstrument, 'strikePrice' | 'putCall'> | { strikePrice?: number; putCall?: 'PUT' | 'CALL' },
  underlyingPrice?: number
): "ITM" | "OTM" | "N/A" => {
  // Log detailed debugging information to help diagnose the issue
  console.log('[PortfolioPage] getOptionStatus input:', { 
    instrumentStrike: instrument.strikePrice, 
    putCall: instrument.putCall, 
    underlyingPrice 
  });
  
  if (underlyingPrice === undefined || instrument.strikePrice === undefined || !instrument.putCall) {
    console.log('[PortfolioPage] getOptionStatus returning N/A due to missing data:', {
      hasUnderlyingPrice: underlyingPrice !== undefined,
      hasStrikePrice: instrument.strikePrice !== undefined,
      hasPutCall: !!instrument.putCall
    });
    return "N/A";
  }
  
  const strike = instrument.strikePrice;
  let status: "ITM" | "OTM" | "N/A" = "N/A";
  
  if (instrument.putCall === "CALL") {
    status = underlyingPrice > strike ? "ITM" : "OTM";
  } else if (instrument.putCall === "PUT") {
    status = underlyingPrice < strike ? "ITM" : "OTM";
  }
  
  console.log('[PortfolioPage] getOptionStatus result:', {
    strike,
    putCall: instrument.putCall,
    underlyingPrice,
    status
  });
  
  return status;
};

// Modified function signature and added retry logic for invalid symbols
async function fetchQuotes(
  symbols: string[],
  isRetryAttempt: boolean = false // Added for retry logic
): Promise<AlpacaQuotesApiResponse> {
  if (symbols.length === 0) {
    // console.log("[PortfolioPage] fetchQuotes: No symbols to fetch, returning empty quotes."); // Optional log
    return { quotes: {} };
  }

  // Sanitize symbols by removing trailing numbers.
  // This applies to the initial call and ensures the list used for retries is also based on sanitized forms.
  const sanitizedSymbols = symbols.map(symbol => symbol.replace(/\\d+$/, ''));
  // console.log(`[PortfolioPage] fetchQuotes: Original symbols: ${symbols.join(',')}, Sanitized symbols for API call: ${sanitizedSymbols.join(',')}`);


  if (sanitizedSymbols.length === 0) {
    // This could happen if all original symbols consisted only of digits or became empty after sanitization.
    // console.log("[PortfolioPage] fetchQuotes: No valid symbols after initial sanitization, returning empty quotes.");
    return { quotes: {} };
  }

  // console.log(`[PortfolioPage] fetchQuotes: Attempting to fetch for symbols: ${sanitizedSymbols.join(',')}`); // Optional log
  const response = await fetch(`/api/alpaca/quotes?symbols=${sanitizedSymbols.join(',')}`); // Use sanitizedSymbols

  if (!response.ok) {
    let errorText = "Failed to read error response text"; // Default error text
    try {
      errorText = await response.text();
    } catch (e) {
      // Log if reading error text itself fails
      console.error("[PortfolioPage] fetchQuotes: Failed to read error text from failed response.", e);
    }
    console.error(`[PortfolioPage] fetchQuotes: API call failed. Status: ${response.status}, Response: ${errorText}`);

    // Retry logic for "invalid symbol" error
    if (!isRetryAttempt && response.status === 400 && errorText.includes("invalid symbol")) {
      // Attempt to extract the invalid symbol. Example error: "{"message":"code=400, message=invalid symbol: SQQQ1"}"
      const invalidSymbolMatch = errorText.match(/invalid symbol: *(\\S+)/);
      if (invalidSymbolMatch && invalidSymbolMatch[1]) {
        // Clean common trailing characters like quotes or braces if the symbol is at the end of a JSON string
        const rawOffendingSymbol = invalidSymbolMatch[1].replace(/["'},]*$/, ''); 
        // Sanitize the offending symbol from the error message to match the format of symbols in sanitizedSymbols list
        const sanitizedOffendingSymbol = rawOffendingSymbol.replace(/\\d+$/, '');
        
        console.warn(`[PortfolioPage] fetchQuotes: Invalid symbol '${rawOffendingSymbol}' (sanitized to '${sanitizedOffendingSymbol}') detected. Filtering and retrying.`);
        
        // Filter from the sanitizedSymbols list that was used for the failed API call.
        const validSymbolsForRetry = sanitizedSymbols.filter(s => s !== sanitizedOffendingSymbol);

        if (validSymbolsForRetry.length > 0 && validSymbolsForRetry.length < sanitizedSymbols.length) {
          // Only retry if symbols were actually removed and some symbols remain
          console.log(`[PortfolioPage] fetchQuotes: Retrying with symbols: ${validSymbolsForRetry.join(',')}`);
          // The recursive call receives a list of symbols that are already sanitized.
          return fetchQuotes(validSymbolsForRetry, true); 
        } else if (validSymbolsForRetry.length === sanitizedSymbols.length) {
          // This case means the symbol reported as invalid wasn't in our list as expected, or regex/cleanup was insufficient.
          console.warn(`[PortfolioPage] fetchQuotes: Matched invalid symbol '${rawOffendingSymbol}' (sanitized to '${sanitizedOffendingSymbol}'), but filtering did not change the symbol list. Original sanitized list: ${sanitizedSymbols.join(',')}. No retry for this error.`);
        } else { // validSymbolsForRetry.length === 0
          console.warn(`[PortfolioPage] fetchQuotes: No valid symbols remaining after filtering '${rawOffendingSymbol}' (sanitized to '${sanitizedOffendingSymbol}'). Returning empty quotes.`);
          return { quotes: {} };
        }
      } else {
        console.warn("[PortfolioPage] fetchQuotes: Received 400 error with 'invalid symbol' text, but could not parse the specific symbol from error: ", errorText, ". No retry.");
      }
    }
    // If not the specific retryable error, or if it's already a retry, or parsing/filtering failed:
    return { quotes: {} };
  }

  // Successful response processing - this part was updated in the previous turn and should be correct
  try {
    const data = await response.json();
    console.log('[PortfolioPage] fetchQuotes raw API response:', JSON.stringify(data));
    
    if (data && typeof data === 'object') {
      // If the response already has the expected structure with quotes property, use it
      if (data.quotes && typeof data.quotes === 'object') {
        console.log('[PortfolioPage] fetchQuotes: API response already has quotes property');
        // Map back to original symbols if necessary, or ensure consuming code handles sanitized symbols
        // For now, we return with sanitized symbols as keys. This might need adjustment
        // if the calling code strictly expects original (unsanitized) symbols.
        // However, the API itself would have used sanitized symbols for the request.
        return data as AlpacaQuotesApiResponse;
      } 
      
      // If it's the direct response from Alpaca (object where keys are symbols and values are quote objects or arrays of quote objects)
      const transformedData: AlpacaQuotesApiResponse = { quotes: {} };
      Object.entries(data).forEach(([symbolFromResponse, value]) => {
        // The symbolFromResponse here is the one Alpaca used (which should be the sanitized one)
        if (typeof value === 'object' && value !== null) {
          // Check if 'value' itself is the quote object or an array of quotes
          let quoteToUse: AlpacaSDKQuote | null = null;

          if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
            // If it's an array of quotes, take the first one.
            quoteToUse = value[0] as AlpacaSDKQuote;
          } else if (!Array.isArray(value) && 'ap' in value && 'bp' in value) { 
            // If it's a single quote object (heuristic: has ap and bp)
            quoteToUse = value as AlpacaSDKQuote;
          } else if (typeof (value as any).quote === 'object' && (value as any).quote !== null) {
            // If value has a nested 'quote' property
            quoteToUse = (value as any).quote as AlpacaSDKQuote;
          }

          if (quoteToUse) {
            // Store with the symbol Alpaca returned (sanitized version)
            transformedData.quotes[symbolFromResponse] = quoteToUse;
          } else {
            console.warn(`[PortfolioPage] fetchQuotes: Could not extract a valid quote for symbol '${symbolFromResponse}' from value:`, value);
          }
        }
      });
      
      console.log('[PortfolioPage] fetchQuotes transformed data structure:', 
        Object.keys(transformedData.quotes).length > 0 
          ? `Found ${Object.keys(transformedData.quotes).length} quote objects` 
          : 'No quotes found after transformation');
      return transformedData;
    }
    
    console.warn('[PortfolioPage] fetchQuotes: API response.json() did not have expected structure. Response data:', data);
    return { quotes: {} }; // Return empty if structure is unexpected
  } catch (e) {
    console.error('[PortfolioPage] fetchQuotes: Error parsing JSON response for successful call:', e);
    return { quotes: {} }; // Return empty on parsing error
  }
}


// Unified Data Structures
interface UnifiedPosition {
  id?: string; 
  broker: 'Schwab' | 'Tradier' | 'Tastytrade'; 
  accountId: string;
  positionId?: string | number; 
  symbolDisplay: string;
  assetTypeDisplay: 'Option' | 'Stock' | string; 
  quantity: number;
  averagePrice?: number;
  marketValue?: number;
  typeColumnDisplay?: 'PUT' | 'CALL' | string; 
  strikePrice?: number;
  dteValue?: number | string;
  optionStatus?: 'ITM' | 'OTM' | 'N/A';
  originalDataSource: SchwabPosition | TradierPosition | TastytradePosition; // Added TastytradePosition
  dte?: number; // Added optional dte property
  underlyingSymbol?: string; // Added
  isOption?: boolean; // Added
}

interface UnifiedAccount {
  broker: 'Schwab' | 'Tradier' | 'Tastytrade'; // Added Tastytrade
  accountNumber: string;
  value?: number;
  positionsCount?: number;
  optionPositionsCount?: number;
  stockPositionsCount?: number; 
  expiringSoonCount?: number;
  inTheMoneyCount?: number;
  rawAccountData: SchwabSecuritiesAccount | TradierAccountProfile | (TastytradeAccount & { balanceData?: TastytradeAccountBalance }); // Added TastytradeAccount with optional balanceData
}

interface CombinedPortfolioData {
  totalPortfolioValue: number;
  totalPositionsCount: number;
  expiringSoonCount: number;
  inTheMoneyCount: number;
  accounts: UnifiedAccount[];
  allOptionPositions: UnifiedPosition[];
  allStockPositions: UnifiedPosition[]; 
  lastUpdated: string;
  sources: ('Schwab' | 'Tradier' | 'Tastytrade')[]; // Added Tastytrade
}

// FilterCriteria and SortableKey remain the same for now
// ... (Keep existing FilterCriteria and SortableKey type definitions) ...
interface FilterCriteria {
  symbol?: string;
  type?: 'PUT' | 'CALL' | '';
  minStrike?: number;
  maxStrike?: number;
  minDTE?: number;
  maxDTE?: number;
  status?: 'ITM' | 'OTM' | '';
  broker?: 'Schwab' | 'Tradier' | 'Tastytrade' | ''; // Added broker filter
}
type SortableKey = 'symbolDisplay' | 'assetTypeDisplay' | 'typeColumnDisplay' | 'strikePrice' | 'dte' | 'quantity' | 'averagePrice' | 'marketValue' | 'status' | 'broker';


// Interface for processed data from each broker
interface ProcessedBrokerData {
  totalPortfolioValue: number;
  totalPositionsCount: number;
  expiringSoonCount: number;
  inTheMoneyCount: number;
  accounts: UnifiedAccount[];
  allOptionPositions: UnifiedPosition[];
  allStockPositions: UnifiedPosition[];
}

// Stub implementation for processAndAdaptTradierData
const processAndAdaptTradierData = (
  sourceData: TradierSourceData | null,
  quotes: AlpacaQuotesApiResponse | null
): ProcessedBrokerData => {
  if (!sourceData || !quotes) return { totalPortfolioValue: 0, totalPositionsCount: 0, expiringSoonCount: 0, inTheMoneyCount: 0, accounts: [], allOptionPositions: [], allStockPositions: [] };
  // Placeholder logic - replace with actual implementation later
  console.log('[processAndAdaptTradierData] Called with sourceData, quotes. (Stub)');
  return { totalPortfolioValue: 0, totalPositionsCount: 0, expiringSoonCount: 0, inTheMoneyCount: 0, accounts: [], allOptionPositions: [], allStockPositions: [] };
};

// Full implementation for processAndAdaptTastytradeData
const processAndAdaptTastytradeData = (
  sourceData: TastytradeSourceData | null,
  quotes: AlpacaQuotesApiResponse | null
): ProcessedBrokerData => {
  const processedData: ProcessedBrokerData = {
    totalPortfolioValue: 0,
    totalPositionsCount: 0,
    expiringSoonCount: 0,
    inTheMoneyCount: 0,
    accounts: [],
    allOptionPositions: [],
    allStockPositions: [],
  };

  if (!sourceData || !sourceData.accounts || sourceData.accounts.length === 0) {
    return processedData;
  }

  console.log('[PortfolioPage] processAndAdaptTastytradeData: Starting processing', { sourceData, quotes });

  sourceData.accounts.forEach(account => {
    const accountNumber = account['account-number'];
    const balanceData = sourceData.balances[accountNumber];
    const accountPositions = sourceData.positions[accountNumber] || [];

    let accountValue = 0;
    if (balanceData && balanceData['net-liquidating-value']) {
      accountValue = parseFloat(balanceData['net-liquidating-value']);
    }

    let unifiedAccountPositionsCount = 0;
    let unifiedOptionPositionsCount = 0;
    let unifiedStockPositionsCount = 0;
    let unifiedExpiringSoonCount = 0;
    let unifiedInTheMoneyCount = 0;

    accountPositions.forEach((pos: TastytradePosition, index: number) => {
      const quantity = parseFloat(pos.quantity);
      const averagePrice = parseFloat(pos['average-open-price']);
      let calculatedMarketValue: number; // Will be calculated below

      const commonPositionProps = {
        id: `${accountNumber}-${pos.symbol}-${index}-${pos['instrument-type']}`, // Made ID more unique
        broker: 'Tastytrade' as 'Tastytrade',
        accountId: accountNumber,
        positionId: pos['id']?.toString() ?? `${pos.symbol}-${index}`,
        symbolDisplay: pos['underlying-symbol'] || pos.symbol,
        quantity: quantity,
        averagePrice: averagePrice,
        originalDataSource: pos,
      };

      if (pos['instrument-type'] === 'Equity Option') {
        const mtmValue = parseFloat(pos['mark-to-market-value']);
        const tastytradeMarkPrice = parseFloat(pos['mark-price']);

        if (!isNaN(mtmValue)) {
          calculatedMarketValue = quantity > 0 ? mtmValue / quantity : mtmValue; // Convert to per unit price
          console.log(`[PortfolioPage] Tasty Option ${pos.symbol} (${pos['underlying-symbol']}): Used mark-to-market-value per unit: ${calculatedMarketValue}`);
        } else if (!isNaN(tastytradeMarkPrice)) {
          // Assuming mark-price for options is already per contract.
          calculatedMarketValue = tastytradeMarkPrice; // Price per unit
          console.log(`[PortfolioPage] Tasty Option ${pos.symbol} (${pos['underlying-symbol']}): Used mark-price per unit: ${tastytradeMarkPrice}. Quantity: ${quantity}. Multiplier: ${pos.multiplier}`);
        } else {
          calculatedMarketValue = NaN;
          console.warn(`[PortfolioPage] Tasty Option ${pos.symbol} (${pos['underlying-symbol']}): mtm-value and mark-price are NaN.`);
        }

        const parsedInfo = parseOptionSymbol(pos.symbol);
        let strikePrice = pos['strike-price'] ? parseFloat(pos['strike-price']) : parsedInfo?.strikePrice;
        let optionType = pos['option-type'] === 'C' ? 'CALL' : pos['option-type'] === 'P' ? 'PUT' : parsedInfo?.putCall;
        let expirationDateStr = pos['expiration-date'];
        let expirationDateObj = expirationDateStr ? new Date(expirationDateStr + 'T00:00:00Z') : parsedInfo?.expirationDate;
        let underlyingSymbol = pos['underlying-symbol'] || parsedInfo?.underlyingSymbol;

        const dteValue = expirationDateObj ? calculateDTE(expirationDateObj) : 'N/A';
        const underlyingQuote = underlyingSymbol ? quotes?.quotes?.[underlyingSymbol] : undefined;
        
        let underlyingPriceForStatus: number | undefined = undefined;
        if (underlyingQuote && typeof underlyingQuote.ap === 'number' && typeof underlyingQuote.bp === 'number') {
            underlyingPriceForStatus = (underlyingQuote.ap + underlyingQuote.bp) / 2;
        }
        
        const optionStatus = getOptionStatus(
          { strikePrice: strikePrice, putCall: optionType as 'PUT' | 'CALL' },
          underlyingPriceForStatus
        );

        const unifiedPos: UnifiedPosition = {
          ...commonPositionProps,
          marketValue: calculatedMarketValue,
          assetTypeDisplay: 'Option',
          typeColumnDisplay: optionType,
          strikePrice: strikePrice,
          dteValue: dteValue,
          dte: typeof dteValue === 'number' ? dteValue : undefined,
          optionStatus: optionStatus,
          underlyingSymbol: underlyingSymbol,
          isOption: true,
        };
        processedData.allOptionPositions.push(unifiedPos);
        unifiedOptionPositionsCount++;
        if (typeof dteValue === 'number' && dteValue <= EXPIRING_SOON_DAYS) {
          unifiedExpiringSoonCount++;
        }
        if (optionStatus === 'ITM') {
          unifiedInTheMoneyCount++;
        }
      } else if (pos['instrument-type'] === 'Equity') {
        const stockSymbol = pos.symbol;
        const quoteInfo = quotes?.quotes?.[stockSymbol];
        const tastytradeMarkPrice = parseFloat(pos['mark-price']);
        let livePrice: number | undefined = undefined;

        if (quoteInfo && typeof quoteInfo.bp === 'number' && !isNaN(quoteInfo.bp)) {
          livePrice = quoteInfo.bp; // Use bid price (bp)
          console.log(`[PortfolioPage] Tasty Equity ${stockSymbol}: Alpaca quote bp: ${quoteInfo.bp}`);
        } else if (quoteInfo && typeof quoteInfo.ap === 'number' && typeof quoteInfo.bp === 'number' && !isNaN(quoteInfo.ap) && !isNaN(quoteInfo.bp)) {
          // Fallback to midpoint if bp specifically isn't good but ap/bp are available
          livePrice = (quoteInfo.ap + quoteInfo.bp) / 2;
          console.warn(`[PortfolioPage] Tasty Equity ${stockSymbol}: Alpaca quote bp was not a number (is ${quoteInfo.bp}). Fell back to midpoint: ${livePrice}`);
        }

        if (livePrice !== undefined && !isNaN(livePrice)) {
          calculatedMarketValue = livePrice; // Price per unit
          console.log(`[PortfolioPage] Tasty Equity ${stockSymbol}: Used Alpaca live price per unit: ${livePrice}. Quantity: ${quantity}.`);
        } else if (!isNaN(tastytradeMarkPrice)) {
          calculatedMarketValue = tastytradeMarkPrice; // Price per unit
          console.warn(`[PortfolioPage] Tasty Equity ${stockSymbol}: Alpaca quote unavailable/NaN (Quote: ${JSON.stringify(quoteInfo)}). Used Tastytrade mark price per unit: ${tastytradeMarkPrice}.`);
        } else {
          calculatedMarketValue = NaN;
          console.warn(`[PortfolioPage] Tasty Equity ${stockSymbol}: Both Alpaca and Tastytrade mark price are unavailable/NaN.`);
        }

        const unifiedPos: UnifiedPosition = {
          ...commonPositionProps,
          symbolDisplay: stockSymbol, // Ensure symbolDisplay is the stock symbol for equities
          marketValue: calculatedMarketValue,
          assetTypeDisplay: 'Equity',
          typeColumnDisplay: 'N/A',
          isOption: false,
        };
        processedData.allStockPositions.push(unifiedPos);
        unifiedStockPositionsCount++;
      } else { // Futures, Crypto, etc.
        const tastytradeMarkPrice = parseFloat(pos['mark-price']);
        const multiplier = pos.multiplier || 1;

        if (!isNaN(tastytradeMarkPrice)) {
          calculatedMarketValue = tastytradeMarkPrice * multiplier; // Price per unit (with multiplier)
          console.log(`[PortfolioPage] Tasty Other (${pos['instrument-type']}) ${pos.symbol}: Used mark price per unit: ${tastytradeMarkPrice} * multiplier ${multiplier} = ${calculatedMarketValue}. Quantity: ${quantity}.`);
        } else {
          calculatedMarketValue = NaN;
          console.warn(`[PortfolioPage] Tasty Other (${pos['instrument-type']}) ${pos.symbol}: mark-price is NaN.`);
        }
        
        const unifiedPos: UnifiedPosition = {
          ...commonPositionProps,
          symbolDisplay: pos.symbol, // Ensure symbolDisplay is the direct symbol
          marketValue: calculatedMarketValue,
          assetTypeDisplay: pos['instrument-type'] || 'Stock',
          typeColumnDisplay: 'N/A',
          isOption: false,
        };
        processedData.allStockPositions.push(unifiedPos);
        unifiedStockPositionsCount++;
      }
      unifiedAccountPositionsCount++;
    });

    processedData.accounts.push({
      broker: 'Tastytrade',
      accountNumber: accountNumber,
      value: accountValue,
      positionsCount: unifiedAccountPositionsCount,
      optionPositionsCount: unifiedOptionPositionsCount,
      stockPositionsCount: unifiedStockPositionsCount,
      expiringSoonCount: unifiedExpiringSoonCount,
      inTheMoneyCount: unifiedInTheMoneyCount,
      rawAccountData: { ...account, balanceData },
    });

    processedData.totalPortfolioValue += accountValue;
    processedData.totalPositionsCount += unifiedAccountPositionsCount;
    processedData.expiringSoonCount += unifiedExpiringSoonCount;
    processedData.inTheMoneyCount += unifiedInTheMoneyCount;
  });

  console.log('[PortfolioPage] processAndAdaptTastytradeData: Processing complete', processedData);
  return processedData;
};


// The main PortfolioPage component
const PortfolioPage: React.FC = () => {
  const [schwabConnected, setSchwabConnected] = useState(false);
  const [tradierConnected, setTradierConnected] = useState(false);
  const { tastytradeConnected, setBrokerConnected } = useBrokerConnections(); // Used hook, get setBrokerConnected

  const [schwabSourceData, setSchwabSourceData] = useState<{ accounts: SchwabAccountAPIItem[] } | null>(null);
  const [tradierSourceData, setTradierSourceData] = useState<TradierSourceData | null>(null);
  const [tastytradeSourceData, setTastytradeSourceData] = useState<TastytradeSourceData | null>(null); // New state for Tastytrade data
  const [underlyingQuotes, setUnderlyingQuotes] = useState<AlpacaQuotesApiResponse | null>(null); // Changed type here

  const [combinedPortfolioData, setCombinedPortfolioData] = useState<CombinedPortfolioData | null>(null);

  const [loadingSources, setLoadingSources] = useState<Set<string>>(new Set());
  const [errorSources, setErrorSources] = useState<Record<string, string | null>>({});

  const [activeTab, setActiveTab] = useState<'all' | 'expiring' | 'itm'>('all');
  const [sortConfig, setSortConfig] = useState<{ key: SortableKey | null; direction: 'ascending' | 'descending' }>({ key: null, direction: 'ascending' });
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  
  const [stockSortConfig, setStockSortConfig] = useState<{ key: SortableKey | null; direction: 'ascending' | 'descending' }>({ key: null, direction: 'ascending' });
  const [stockFilterCriteria, setStockFilterCriteria] = useState<{symbol?: string, broker?: 'Schwab' | 'Tradier' | ''}>({}); // Added broker to stockFilterCriteria
  const [showStockFilterPanel, setShowStockFilterPanel] = useState(false);
  const [activeStockFilters, setActiveStockFilters] = useState<string[]>([]);

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null); // <-- New state for selected account

  // Effect for fetching Schwab data
  useEffect(() => {
    if (schwabConnected) {
      setLoadingSources(prev => new Set(prev).add('Schwab'));
      setErrorSources(prev => ({ ...prev, Schwab: null }));
      fetchBackendSchwabProfile()
        .then(profileData => {
          setSchwabSourceData(profileData);
        })
        .catch(err => {
          console.error("Error fetching Schwab data:", err);
          setErrorSources(prev => ({ ...prev, Schwab: err.message || "Failed to load Schwab profile data." }));
        })
        .finally(() => {
          setLoadingSources(prev => {
            const newSet = new Set(prev);
            newSet.delete('Schwab');
            return newSet;
          });
        });
    } else {
      setSchwabSourceData(null); // Clear data if disconnected
    }
  }, [schwabConnected]);

  // Effect for fetching Tradier data
  useEffect(() => {
    if (tradierConnected) {
      setLoadingSources(prev => new Set(prev).add('Tradier'));
      setErrorSources(prev => ({ ...prev, Tradier: null }));
      
      const fetchTradierData = async () => {
        console.log("[PortfolioPage] fetchTradierData: Initiating individual fetches.");
        try {
          const tradierApiKey = localStorage.getItem('tradierApiKey');
          console.log("[PortfolioPage] fetchTradierData: API Key from localStorage:", tradierApiKey ? `present` : "MISSING or null");

          if (!tradierApiKey) {
            console.error("[PortfolioPage] fetchTradierData: Error - Tradier API key not found.");
            throw new Error("Tradier API key not found in localStorage.");
          }

          console.log("[PortfolioPage] fetchTradierData: Fetching Tradier User Profile...");
          const userProfile = await getTradierUserProfile(tradierApiKey);
          console.log("[PortfolioPage] fetchTradierData: Tradier User Profile fetch successful.");

          if (!userProfile || !userProfile.account) {
            console.error("[PortfolioPage] Tradier profile or accounts not found after fetching profile.", userProfile);
            throw new Error("Tradier profile or accounts not found.");
          }

          console.log("[PortfolioPage] fetchTradierData: Fetching Tradier User Balances...");
          const userBalances = await getTradierUserBalances(tradierApiKey);
          console.log("[PortfolioPage] fetchTradierData: Tradier User Balances fetch successful.");

          console.log("[PortfolioPage] fetchTradierData: Fetching Tradier Positions...");
          const userPositions = await getTradierPositions(tradierApiKey);
          console.log("[PortfolioPage] fetchTradierData: Tradier User Positions fetch successful.", userPositions);

          // Define newTradierSourceData using the fetched data
          const newTradierSourceData: TradierSourceData = {
            profile: userProfile, // userProfile was fetched earlier in this try block
            balances: userBalances,
            positions: userPositions
          };

          setTradierSourceData(newTradierSourceData);
          console.log("[PortfolioPage] fetchTradierData: setTradierSourceData called successfully.");

        } catch (err: any) {
          console.error("[PortfolioPage] fetchTradierData: CATCH BLOCK - Error details:", err);
          setErrorSources(prev => ({ ...prev, Tradier: (err?.message as string) || "Failed to load Tradier portfolio data." }));
        } finally {
          console.log("[PortfolioPage] fetchTradierData: FINALLY BLOCK - Executing.");
          setLoadingSources(prev => {
            const newSet = new Set(prev);
            newSet.delete('Tradier');
            return newSet;
          });
        }
      };
      fetchTradierData();
    } else {
      console.log("[PortfolioPage] Tradier not connected, clearing tradierSourceData.");
      setTradierSourceData(null); // Clear data if disconnected
    }
  }, [tradierConnected]);

  // Effect for fetching Tastytrade data
  useEffect(() => {
    console.log(`[PortfolioPage] Tastytrade useEffect triggered. tastytradeConnected: ${tastytradeConnected}`); // Added log

    if (tastytradeConnected) {
      setLoadingSources(prev => new Set(prev).add('Tastytrade'));
      setErrorSources(prev => ({ ...prev, Tastytrade: null }));

      const fetchTastytradeData = async () => {
        try {
          console.log("[PortfolioPage] fetchTastytradeData: Fetching Tastytrade accounts...");
          const rawAccountsResponse = await tastytradeClient.getTastytradeAccounts(); 
          console.log("[PortfolioPage] fetchTastytradeData: Tastytrade raw accounts response successful.", rawAccountsResponse);

          // rawAccountsResponse is already the array of accounts
          const accounts = rawAccountsResponse; 
          console.log("[PortfolioPage] fetchTastytradeData: Extracted accounts (now directly from response, before filtering):", JSON.stringify(accounts, null, 2));

          if (!accounts || accounts.length === 0) {
            console.warn("[PortfolioPage] No Tastytrade accounts found after extraction.");
            setTastytradeSourceData({ accounts: [], balances: {}, positions: {} });
            return;
          }

          // Filter for accounts with valid, non-empty string account numbers
          const validAccounts = accounts.filter((acc, index) => {
            console.log(`[PortfolioPage] Filtering account index: ${index}, account object:`, acc);
            if (!acc) {
              console.log(`[PortfolioPage] Account index: ${index} is falsy.`);
              return false;
            }
            const accNum = acc['account-number'];
            console.log(`[PortfolioPage] Account index: ${index}, account-number raw:`, accNum);
            const isString = typeof accNum === 'string';
            console.log(`[PortfolioPage] Account index: ${index}, typeof account-number: ${typeof accNum}, isString: ${isString}`);
            const isNotEmpty = isString && accNum.trim() !== '';
            console.log(`[PortfolioPage] Account index: ${index}, trimmed account-number: "${isString ? accNum.trim() : 'N/A'}", isNotEmpty: ${isNotEmpty}`);
            const isValid = acc && isString && isNotEmpty;
            console.log(`[PortfolioPage] Account index: ${index}, passes filter: ${isValid}`);
            return isValid;
          });

          if (validAccounts.length === 0) {
            console.warn("[PortfolioPage] fetchTastytradeData: No Tastytrade accounts with valid account numbers found after filtering. Original extracted accounts:", accounts);
            setTastytradeSourceData({ accounts: [], balances: {}, positions: {} });
            return;
          }
          
          const accountNumbers = validAccounts.map(acc => acc['account-number']);
          console.log("[PortfolioPage] fetchTastytradeData: Fetching balances for valid account numbers:", accountNumbers);
          
          // This check might be slightly redundant if validAccounts.length > 0 ensures accountNumbers.length > 0,
          // but it's a safe guard.
          if (accountNumbers.length === 0) { 
             console.warn("[PortfolioPage] fetchTastytradeData: No valid account numbers to fetch data for, after mapping from validAccounts. This should not occur if validAccounts was not empty and map was correct.");
             setTastytradeSourceData({ accounts: validAccounts, balances: {}, positions: {} });
             return;
          }

          const balancesPromises = accountNumbers.map(accNum => 
            tastytradeClient.getTastytradeAccountBalance(accNum).then(bal => ({ [accNum]: bal }))
          );
          const balancesArray = await Promise.all(balancesPromises);
          const balances = Object.assign({}, ...balancesArray);
          console.log("[PortfolioPage] fetchTastytradeData: Tastytrade balances fetch successful.", balances);

          console.log("[PortfolioPage] fetchTastytradeData: Fetching positions for account numbers:", accountNumbers);
          const positionsPromises = accountNumbers.map(accNum => 
            tastytradeClient.getTastytradeAccountPositions(accNum).then(pos => ({ [accNum]: pos }))
          );
          const positionsArray = await Promise.all(positionsPromises);
          const positions = Object.assign({}, ...positionsArray);
          console.log("[PortfolioPage] fetchTastytradeData: Tastytrade positions fetch successful.", positions);

          setTastytradeSourceData({ accounts: validAccounts, balances, positions }); // Use validAccounts
          console.log("[PortfolioPage] fetchTastytradeData: setTastytradeSourceData called successfully with filtered accounts.");

        } catch (err: any) {
          console.error("[PortfolioPage] fetchTastytradeData: CATCH BLOCK - Error details:", err);
          
          let displayErrorMessage: string | null = err.message || "Failed to load Tastytrade portfolio data.";

          if (err.message) {
            if (err.message.includes("access token is not set")) {
              console.log("[PortfolioPage] fetchTastytradeData: Tastytrade access token is not set. Clearing token and disconnecting.");
              tastytradeClient.clearTastyTradeAccessToken(); // Corrected casing
              setBrokerConnected('tastytradeConnected', false);
              displayErrorMessage = null; // Don't display this specific error
            } else if (err.message.includes("401")) {
              console.log("[PortfolioPage] fetchTastytradeData: Detected 401 error. Clearing Tastytrade token and disconnecting.");
              tastytradeClient.clearTastyTradeAccessToken(); // Corrected casing
              setBrokerConnected('tastytradeConnected', false);
              displayErrorMessage = "Tastytrade authentication failed. Please reconnect."; // User-friendly message for 401
            }
            // For other errors, displayErrorMessage remains err.message or the default
          }
          
          setErrorSources(prev => ({ ...prev, Tastytrade: displayErrorMessage }));

        } finally {
          console.log("[PortfolioPage] fetchTastytradeData: FINALLY BLOCK - Executing.");
          setLoadingSources(prev => {
            const newSet = new Set(prev);
            newSet.delete('Tastytrade');
            return newSet;
          });
        }
      };

      fetchTastytradeData();
    } else {
      console.log("[PortfolioPage] Tastytrade not connected (inside useEffect), clearing tastytradeSourceData."); // Added log
      setTastytradeSourceData(null);
    }
  }, [tastytradeConnected]);

  // Effect for fetching underlying quotes
  useEffect(() => {
    const symbolsToFetch = new Set<string>();

    // Collect symbols from Schwab data
    schwabSourceData?.accounts.forEach(acc => {
      acc.securitiesAccount?.positions?.forEach(pos => {
        const instrument = pos.instrument;
        let effectiveUnderlyingSymbol: string | undefined = undefined;
        const isSchwabApiOption = instrument.type === 'OPTION' || instrument.type === 'VANILLA' || (typeof instrument.strikePrice === 'number' && instrument.optionExpirationDate);
        let isPotentiallyOption = isSchwabApiOption;

        if (instrument.symbol) {
          const parsedInfo = parseOptionSymbol(instrument.symbol);
          if (parsedInfo) {
            effectiveUnderlyingSymbol = parsedInfo.underlyingSymbol;
            isPotentiallyOption = true;
          }
        }
        if (!effectiveUnderlyingSymbol && isSchwabApiOption) {
          effectiveUnderlyingSymbol = instrument.underlyingSymbol;
        }
        if (effectiveUnderlyingSymbol && isPotentiallyOption) {
          symbolsToFetch.add(effectiveUnderlyingSymbol);
        }
      });
    });

    // Collect symbols from Tradier data
    if (tradierSourceData?.positions) {
      Object.values(tradierSourceData.positions).flat().forEach(pos => {
        const parsedInfo = parseOptionSymbol(pos.symbol);
        if (parsedInfo) { // It's an option
          symbolsToFetch.add(parsedInfo.underlyingSymbol);
        }
      });
    }

    // Collect symbols from Tastytrade data
    if (tastytradeSourceData?.positions) {
      Object.values(tastytradeSourceData.positions).flat().forEach(pos => {
        if (pos['instrument-type'] === 'Equity Option' || pos['instrument-type'] === 'Future Option') {
          if (pos['underlying-symbol']) {
            symbolsToFetch.add(pos['underlying-symbol']);
          }
        } else if (pos.symbol) { // For Equity, Future, Cryptocurrency etc.
          symbolsToFetch.add(pos.symbol);
        }
      });
    }

    // console.log('[PortfolioPage] Symbols to fetch for quotes:', Array.from(symbolsToFetch)); // This is effectively duplicated by the next line
    console.log('[PortfolioPage] Effect for fetching quotes - symbolsToFetch:', Array.from(symbolsToFetch));


    if (symbolsToFetch.size > 0) {
      setLoadingSources(prev => new Set(prev).add('Quotes'));
      setErrorSources(prev => ({ ...prev, Quotes: null }));
      fetchQuotes(Array.from(symbolsToFetch))
        .then(quotesData => {
          // console.log('[PortfolioPage] Raw quotesData received from fetchQuotes:', JSON.stringify(quotesData)); // Original commented out log
          // The log below was '[PortfolioPage] fetchQuotes resolved with data:', JSON.stringify(quotesData)
          console.log('[PortfolioPage] fetchQuotes resolved with data (RAW RETURN):', quotesData);
          console.log('[PortfolioPage] fetchQuotes resolved with data (STRINGIFIED):', JSON.stringify(quotesData));
          setUnderlyingQuotes(quotesData);
          console.log('[PortfolioPage] underlyingQuotes state has been updated after fetchQuotes.');
        })
        .catch(quoteError => {
          console.error("[PortfolioPage] Error in fetchQuotes promise chain:", quoteError);
          setErrorSources(prev => ({ ...prev, Quotes: 'Failed to load underlying market data.' }));
          setUnderlyingQuotes({ quotes: {} }); // Explicitly set to empty on error here
        })
        .finally(() => {
          setLoadingSources(prev => {
            const newSet = new Set(prev);
            newSet.delete('Quotes');
            return newSet;
          });
        });
    } else {
      console.log('[PortfolioPage] No symbols to fetch, setting underlyingQuotes to empty. (Effect Path: NO_SYMBOLS_TO_FETCH)');
      setUnderlyingQuotes({ quotes: {} }); // No symbols, clear quotes or set to empty
    }
  }, [schwabSourceData, tradierSourceData, tastytradeSourceData]); // Added tastytradeSourceData to dependencies


  // Processing and Merging Data
  const processAndAdaptSchwabData = useCallback((
    profile: { accounts: SchwabAccountAPIItem[] },
    quotes: AlpacaQuotesApiResponse | null // Changed type here
  ): Omit<CombinedPortfolioData, 'lastUpdated' | 'sources'> => {
    console.log('[PortfolioPage] processAndAdaptSchwabData - Quotes object received:', quotes ? (quotes.quotes ? 'Quotes map available' : 'Quotes map MISSING') : 'Quotes object NULL');
    let totalPortfolioValue = 0;
    const allOptionPositions: UnifiedPosition[] = [];
    const allStockPositions: UnifiedPosition[] = [];
    const unifiedAccounts: UnifiedAccount[] = [];
    let globalExpiringSoonCount = 0;
    let globalInTheMoneyCount = 0;

    profile.accounts.forEach(accResponse => {
      const secAccount = accResponse.securitiesAccount;
      if (!secAccount) return;

      const accountValue = secAccount.currentBalances?.liquidationValue || secAccount.initialBalances?.liquidationValue || 0;
      totalPortfolioValue += accountValue;
      
      let accountExpiringSoon = 0;
      let accountITM = 0;
      let accountOptionPositionsCount = 0;
      let accountStockPositionsCount = 0;

      (secAccount.positions || []).forEach(p => {
        const originalInstrument = p.instrument;
        let symbolDisplay = originalInstrument.underlyingSymbol || originalInstrument.symbol;
        let typeColumnDisplay: string | undefined = originalInstrument.putCall;
        let dteNum: number | string = 'N/A';
        let optionStatus: "ITM" | "OTM" | "N/A" = "N/A";
        let strikePrice = originalInstrument.strikePrice;
        let assetTypeDisplay: UnifiedPosition['assetTypeDisplay'] = 'Stock';

        const isSchwabApiOption = originalInstrument.type === 'OPTION' || originalInstrument.type === 'VANILLA' || (typeof originalInstrument.strikePrice === 'number' && originalInstrument.optionExpirationDate);
        let isEffectivelyOption = isSchwabApiOption;

        if (originalInstrument.symbol) {
            const parsedInfo = parseOptionSymbol(originalInstrument.symbol);
            if (parsedInfo) {
                isEffectivelyOption = true;
                symbolDisplay = parsedInfo.underlyingSymbol;
                typeColumnDisplay = parsedInfo.putCall;
                strikePrice = parsedInfo.strikePrice;
                dteNum = calculateDTE(parsedInfo.expirationDate);
            } else if (isSchwabApiOption && originalInstrument.optionExpirationDate) {
                const expiryDateFromField = new Date(originalInstrument.optionExpirationDate);
                const utcExpirationDate = new Date(Date.UTC(expiryDateFromField.getUTCFullYear(), expiryDateFromField.getUTCMonth(), expiryDateFromField.getUTCDate()));
                dteNum = calculateDTE(utcExpirationDate);
            }
        } else if (isSchwabApiOption && originalInstrument.optionExpirationDate) {
            const expiryDateFromField = new Date(originalInstrument.optionExpirationDate);
            const utcExpirationDate = new Date(Date.UTC(expiryDateFromField.getUTCFullYear(), expiryDateFromField.getUTCMonth(), expiryDateFromField.getUTCDate()));
            dteNum = calculateDTE(utcExpirationDate);
        }

        if (isEffectivelyOption) {
          assetTypeDisplay = 'Option';
          const normalizedSymbol = symbolDisplay ? symbolDisplay.trim().toUpperCase() : '';
          
          const quoteInfoForSymbol = quotes?.quotes?.[normalizedSymbol]; // Corrected access
          let underlyingPrice: number | undefined = undefined;
          if (quoteInfoForSymbol && typeof quoteInfoForSymbol.bp === 'number' && typeof quoteInfoForSymbol.ap === 'number') {
            underlyingPrice = (quoteInfoForSymbol.bp + quoteInfoForSymbol.ap) / 2; // Corrected access
          }

          // ADD THIS LOGGING BLOCK for Schwab:
          console.log(`[DEBUG OPTION STATUS] Broker: Schwab, Account: ${secAccount.accountNumber}, Option Symbol: ${originalInstrument.symbol}, Parsed Underlying: '${symbolDisplay}', Normalized: '${normalizedSymbol}'`);
          console.log(`[DEBUG OPTION STATUS] Quote object available:`, quotes?.quotes ? 'Yes' : 'No');
          if (quotes?.quotes) {
            console.log(`[DEBUG OPTION STATUS] Available symbols in quotes:`, Object.keys(quotes.quotes));
          }
          console.log(`[DEBUG OPTION STATUS] Quote found for '${normalizedSymbol}':`, quoteInfoForSymbol ? 'Yes' : 'No');
          if (quoteInfoForSymbol) {
            console.log(`[DEBUG OPTION STATUS] Quote data:`, { ap: quoteInfoForSymbol.ap, bp: quoteInfoForSymbol.bp, underlyingPrice });
          }
          
          optionStatus = getOptionStatus({ strikePrice, putCall: typeColumnDisplay as 'PUT' | 'CALL' }, underlyingPrice);
          console.log(`[DEBUG OPTION STATUS] Final status - Symbol: ${symbolDisplay}, Strike: ${strikePrice}, Type: ${typeColumnDisplay}, Price: ${underlyingPrice}, Status: ${optionStatus}`);
          
          if (typeof dteNum === "number" && dteNum <= EXPIRING_SOON_DAYS) accountExpiringSoon++;
          if (optionStatus === "ITM") accountITM++;
        } else { // Stock
            typeColumnDisplay = undefined;
            strikePrice = undefined;
            dteNum = 'N/A';
            optionStatus = 'N/A';
            symbolDisplay = originalInstrument.symbol || '';
        }
        
        // Calculate market value as current price per unit (not total market value)
        let calculatedMarketValue: number | undefined = undefined;
        const quantity = p.longQuantity || p.shortQuantity || 0;
        
        if (!isEffectivelyOption) {
          // For stocks, use current market price per unit from quotes (bp - bid price)
          const stockSymbol = originalInstrument.symbol || '';
          const quoteInfo = quotes?.quotes?.[stockSymbol.toUpperCase()];
          if (quoteInfo && typeof quoteInfo.bp === 'number' && !isNaN(quoteInfo.bp)) {
            calculatedMarketValue = quoteInfo.bp; // Price per unit
            console.log(`[PortfolioPage] Schwab Stock ${stockSymbol}: Used Alpaca quote bp per unit: ${quoteInfo.bp}. Quantity: ${quantity}.`);
          } else if (quoteInfo && typeof quoteInfo.ap === 'number' && typeof quoteInfo.bp === 'number' && !isNaN(quoteInfo.ap) && !isNaN(quoteInfo.bp)) {
            // Fallback to midpoint if bp specifically isn't good but ap/bp are available
            const midpoint = (quoteInfo.ap + quoteInfo.bp) / 2;
            calculatedMarketValue = midpoint; // Price per unit
            console.log(`[PortfolioPage] Schwab Stock ${stockSymbol}: Used Alpaca quote midpoint per unit: ${midpoint}. Quantity: ${quantity}.`);
          } else {
            // Ultimate fallback to broker-provided market value per unit
            calculatedMarketValue = quantity > 0 ? p.marketValue / quantity : p.marketValue;
            console.warn(`[PortfolioPage] Schwab Stock ${stockSymbol}: No quote available, using broker market value per unit fallback: ${calculatedMarketValue}`);
          }
        } else {
          // For options, fallback to broker-provided market value (options quotes are more complex)
          calculatedMarketValue = p.marketValue;
        }
        
        const unifiedPos: UnifiedPosition = {
          broker: 'Schwab',
          accountId: secAccount.accountNumber,
          positionId: p.positionId,
          symbolDisplay,
          assetTypeDisplay,
          quantity: quantity,
          averagePrice: p.averagePrice,
          marketValue: calculatedMarketValue,
          typeColumnDisplay,
          strikePrice,
          dteValue: dteNum,
          optionStatus,
          originalDataSource: p,
        };

        if (isEffectivelyOption) {
          allOptionPositions.push(unifiedPos);
          accountOptionPositionsCount++;
        } else {
          allStockPositions.push(unifiedPos);
          accountStockPositionsCount++;
        }
      });
      
      globalExpiringSoonCount += accountExpiringSoon;
      globalInTheMoneyCount += accountITM;

      unifiedAccounts.push({
        broker: 'Schwab',
        accountNumber: secAccount.accountNumber,
        value: accountValue,
        positionsCount: secAccount.positions?.length || 0,
        optionPositionsCount: accountOptionPositionsCount,
        stockPositionsCount: accountStockPositionsCount,
        expiringSoonCount: accountExpiringSoon,
        inTheMoneyCount: accountITM,
        rawAccountData: secAccount,
      });
    });

    return {
      totalPortfolioValue,
      totalPositionsCount: allOptionPositions.length + allStockPositions.length,
      expiringSoonCount: globalExpiringSoonCount,
      inTheMoneyCount: globalInTheMoneyCount,
      accounts: unifiedAccounts,
      allOptionPositions,
      allStockPositions,
    };
  }, []);

  const processAndAdaptTradierData = useCallback((
    data: TradierSourceData,
    quotes: AlpacaQuotesApiResponse | null // Changed type here
  ): Omit<CombinedPortfolioData, 'lastUpdated' | 'sources'> => {
    console.log(`[PortfolioPage] processAndAdaptTradierData - Quotes object received:`, quotes ? (quotes.quotes ? 'Quotes map available' : 'Quotes map MISSING') : 'Quotes object NULL');
    let totalPortfolioValue = 0;
    const allOptionPositions: UnifiedPosition[] = [];
    const allStockPositions: UnifiedPosition[] = [];
    const unifiedAccounts: UnifiedAccount[] = [];
    let globalExpiringSoonCount = 0;
    let globalInTheMoneyCount = 0;

    // The profile object itself is what we need, not profile.profile
    const tradierProfile = data.profile;
    if (!tradierProfile || !tradierProfile.account) {
        console.warn("[PortfolioPage] processAndAdaptTradierData: Tradier profile or account data is missing.", tradierProfile);
        return { /* return empty state */ totalPortfolioValue: 0, totalPositionsCount: 0, expiringSoonCount: 0, inTheMoneyCount: 0, accounts: [], allOptionPositions: [], allStockPositions: [] };
    }

    const tradierAccounts = Array.isArray(tradierProfile.account) ? tradierProfile.account : [tradierProfile.account];

    tradierAccounts.forEach(accProfile => {
      // accProfile is now the actual account object from the profile
      const accNum = accProfile.account_number;
      if (!accNum) {
        console.warn("[PortfolioPage] Tradier account profile missing account_number:", accProfile);
        return;
      }
      const accBalancesData = data.balances[accNum]; // This is the UserBalances object for the account
      const accPositions = data.positions[accNum] || [];
      
      // Correctly access total_equity directly from the UserBalances object
      const accountValue = accBalancesData?.total_equity || 0;
      totalPortfolioValue += accountValue;

      let accountExpiringSoon = 0;
      let accountITM = 0;
      let accountOptionPositionsCount = 0;
      let accountStockPositionsCount = 0;

      accPositions.forEach(p => {
        const parsedInfo = parseOptionSymbol(p.symbol);
        let assetTypeDisplay: UnifiedPosition['assetTypeDisplay'] = 'Stock';
        let symbolDisplay = p.symbol;
        let typeColumnDisplay: string | undefined = undefined;
        let strikePrice: number | undefined = undefined;
        let dteNum: number | string = 'N/A';
        let optionStatus: "ITM" | "OTM" | "N/A" = "N/A";

        if (parsedInfo) { // It's an option
          assetTypeDisplay = 'Option';
          symbolDisplay = parsedInfo.underlyingSymbol;
          typeColumnDisplay = parsedInfo.putCall;
          strikePrice = parsedInfo.strikePrice;
          dteNum = calculateDTE(parsedInfo.expirationDate);

          const normalizedSymbol = symbolDisplay ? symbolDisplay.trim().toUpperCase() : '';
          const quoteInfoForSymbol = quotes?.quotes?.[normalizedSymbol]; // Corrected access
          let underlyingPrice: number | undefined = undefined;
          if (quoteInfoForSymbol && typeof quoteInfoForSymbol.bp === 'number' && typeof quoteInfoForSymbol.ap === 'number') {
            underlyingPrice = (quoteInfoForSymbol.bp + quoteInfoForSymbol.ap) / 2; // Corrected access
          }

          // ADD THIS LOGGING BLOCK for Tradier:
          console.log(`[DEBUG OPTION STATUS] Broker: Tradier, Account: ${accNum}, Option Symbol: ${p.symbol}, Parsed Underlying: '${symbolDisplay}', Normalized: '${normalizedSymbol}'`);
          console.log(`[DEBUG OPTION STATUS] Quote object available:`, quotes?.quotes ? 'Yes' : 'No');
          if (quotes?.quotes) {
            console.log(`[DEBUG OPTION STATUS] Available symbols in quotes:`, Object.keys(quotes.quotes));
          }
          console.log(`[DEBUG OPTION STATUS] Quote found for '${normalizedSymbol}':`, quoteInfoForSymbol ? 'Yes' : 'No');
          if (quoteInfoForSymbol) {
            console.log(`[DEBUG OPTION STATUS] Quote data:`, { ap: quoteInfoForSymbol.ap, bp: quoteInfoForSymbol.bp, underlyingPrice });
          }
          
          optionStatus = getOptionStatus({ strikePrice, putCall: typeColumnDisplay as 'PUT' | 'CALL' }, underlyingPrice);
          console.log(`[DEBUG OPTION STATUS] Final status - Symbol: ${symbolDisplay}, Strike: ${strikePrice}, Type: ${typeColumnDisplay}, Price: ${underlyingPrice}, Status: ${optionStatus}`);

          optionStatus = getOptionStatus({ strikePrice, putCall: typeColumnDisplay as 'PUT' | 'CALL' }, underlyingPrice);
          console.log(`[DEBUG OPTION STATUS] Resulting optionStatus for '${normalizedSymbol}': ${optionStatus}`);
          
          if (typeof dteNum === "number" && dteNum <= EXPIRING_SOON_DAYS) accountExpiringSoon++;
          if (optionStatus === "ITM") accountITM++;
        }
        
        // Calculate market value as current price per unit (not total market value)
        let calculatedMarketValue: number | undefined = undefined;
        if (assetTypeDisplay === 'Stock') {
          // For stocks, use current market price per unit from quotes (bp - bid price)
          const quoteInfo = quotes?.quotes?.[symbolDisplay.toUpperCase()];
          if (quoteInfo && typeof quoteInfo.bp === 'number' && !isNaN(quoteInfo.bp)) {
            calculatedMarketValue = quoteInfo.bp; // Price per unit
            console.log(`[PortfolioPage] Tradier Stock ${symbolDisplay}: Used Alpaca quote bp per unit: ${quoteInfo.bp}. Quantity: ${p.quantity}.`);
          } else if (quoteInfo && typeof quoteInfo.ap === 'number' && typeof quoteInfo.bp === 'number' && !isNaN(quoteInfo.ap) && !isNaN(quoteInfo.bp)) {
            // Fallback to midpoint if bp specifically isn't good but ap/bp are available
            const midpoint = (quoteInfo.ap + quoteInfo.bp) / 2;
            calculatedMarketValue = midpoint; // Price per unit
            console.log(`[PortfolioPage] Tradier Stock ${symbolDisplay}: Used Alpaca quote midpoint per unit: ${midpoint}. Quantity: ${p.quantity}.`);
          } else {
            // Ultimate fallback to cost basis per unit
            calculatedMarketValue = p.cost_basis;
            console.warn(`[PortfolioPage] Tradier Stock ${symbolDisplay}: No quote available, using cost basis per unit fallback: ${calculatedMarketValue}`);
          }
        } else {
          // For options, fallback to cost basis per unit (options quotes are more complex)
          calculatedMarketValue = p.cost_basis;
        }

        const unifiedPos: UnifiedPosition = {
          broker: 'Tradier',
          accountId: accNum,
          positionId: p.id,
          symbolDisplay,
          assetTypeDisplay,
          quantity: p.quantity,
          averagePrice: p.cost_basis, // Tradier uses cost_basis
          marketValue: calculatedMarketValue,
          typeColumnDisplay,
          strikePrice,
          dteValue: dteNum,
          optionStatus,
          originalDataSource: p,
        };

        if (assetTypeDisplay === 'Option') {
          allOptionPositions.push(unifiedPos);
          accountOptionPositionsCount++;
        } else {
          allStockPositions.push(unifiedPos);
          accountStockPositionsCount++;
        }
      });

      globalExpiringSoonCount += accountExpiringSoon;
      globalInTheMoneyCount += accountITM;

      unifiedAccounts.push({
        broker: 'Tradier',
        accountNumber: accNum,
        value: accountValue, // Ensure this uses the corrected accountValue
        positionsCount: accPositions.length,
        optionPositionsCount: accountOptionPositionsCount,
        stockPositionsCount: accountStockPositionsCount,
        expiringSoonCount: accountExpiringSoon,
        inTheMoneyCount: accountITM,
        rawAccountData: accProfile,
      });
    });
    
    const result = {
      totalPortfolioValue,
      totalPositionsCount: allOptionPositions.length + allStockPositions.length,
      expiringSoonCount: globalExpiringSoonCount,
      inTheMoneyCount: globalInTheMoneyCount,
      accounts: unifiedAccounts,
      allOptionPositions,
      allStockPositions,
    };
    console.log("[PortfolioPage] processAndAdaptTradierData processing complete.");
    return result;
  }, []);

  const processAndAdaptTastytradeData = useCallback((
    data: TastytradeSourceData | null,
    quotes: AlpacaQuotesApiResponse | null
  ): Omit<CombinedPortfolioData, 'lastUpdated' | 'sources'> => {
    const processedData: ProcessedBrokerData = {
      totalPortfolioValue: 0,
      totalPositionsCount: 0,
      expiringSoonCount: 0,
      inTheMoneyCount: 0,
      accounts: [],
      allOptionPositions: [],
      allStockPositions: [],
    };

    if (!data || !data.accounts || data.accounts.length === 0) {
      return processedData;
    }

    console.log('[PortfolioPage] processAndAdaptTastytradeData: Starting processing', { data, quotes });

    data.accounts.forEach(account => {
      const accountNumber = account['account-number'];
      const balanceData = data.balances[accountNumber];
      const accountPositions = data.positions[accountNumber] || [];

      let accountValue = 0;
      if (balanceData && balanceData['net-liquidating-value']) {
        accountValue = parseFloat(balanceData['net-liquidating-value']);
      }

      let unifiedAccountPositionsCount = 0;
      let unifiedOptionPositionsCount = 0;
      let unifiedStockPositionsCount = 0;
      let unifiedExpiringSoonCount = 0;
      let unifiedInTheMoneyCount = 0;

      accountPositions.forEach((pos: TastytradePosition, index: number) => {
        const quantity = parseFloat(pos.quantity);
        const averagePrice = parseFloat(pos['average-open-price']);
        let calculatedMarketValue: number; // Will be calculated below

        const commonPositionProps = {
          id: `${accountNumber}-${pos.symbol}-${index}-${pos['instrument-type']}`, // Made ID more unique
          broker: 'Tastytrade' as 'Tastytrade',
          accountId: accountNumber,
          positionId: pos['id']?.toString() ?? `${pos.symbol}-${index}`,
          symbolDisplay: pos['underlying-symbol'] || pos.symbol,
          quantity: quantity,
          averagePrice: averagePrice,
          originalDataSource: pos,
        };

        if (pos['instrument-type'] === 'Equity Option') {
          const mtmValue = parseFloat(pos['mark-to-market-value']);
          const tastytradeMarkPrice = parseFloat(pos['mark-price']);

          if (!isNaN(mtmValue)) {
            calculatedMarketValue = quantity > 0 ? mtmValue / quantity : mtmValue; // Convert to per unit price
            console.log(`[PortfolioPage] Tasty Option ${pos.symbol} (${pos['underlying-symbol']}): Used mark-to-market-value per unit: ${calculatedMarketValue}`);
          } else if (!isNaN(tastytradeMarkPrice)) {
            // Assuming mark-price for options is already per contract.
            calculatedMarketValue = tastytradeMarkPrice; // Price per unit
            console.log(`[PortfolioPage] Tasty Option ${pos.symbol} (${pos['underlying-symbol']}): Used mark-price per unit: ${tastytradeMarkPrice}. Quantity: ${quantity}. Multiplier: ${pos.multiplier}`);
          } else {
            calculatedMarketValue = NaN;
            console.warn(`[PortfolioPage] Tasty Option ${pos.symbol} (${pos['underlying-symbol']}): mtm-value and mark-price are NaN.`);
          }

          const parsedInfo = parseOptionSymbol(pos.symbol);
          let strikePrice = pos['strike-price'] ? parseFloat(pos['strike-price']) : parsedInfo?.strikePrice;
          let optionType = pos['option-type'] === 'C' ? 'CALL' : pos['option-type'] === 'P' ? 'PUT' : parsedInfo?.putCall;
          let expirationDateStr = pos['expiration-date'];
          let expirationDateObj = expirationDateStr ? new Date(expirationDateStr + 'T00:00:00Z') : parsedInfo?.expirationDate;
          let underlyingSymbol = pos['underlying-symbol'] || parsedInfo?.underlyingSymbol;

          const dteValue = expirationDateObj ? calculateDTE(expirationDateObj) : 'N/A';
          const underlyingQuote = underlyingSymbol ? quotes?.quotes?.[underlyingSymbol] : undefined;
          
          let underlyingPriceForStatus: number | undefined = undefined;
          if (underlyingQuote && typeof underlyingQuote.ap === 'number' && typeof underlyingQuote.bp === 'number') {
              underlyingPriceForStatus = (underlyingQuote.ap + underlyingQuote.bp) / 2;
          }
          
          const optionStatus = getOptionStatus(
            { strikePrice: strikePrice, putCall: optionType as 'PUT' | 'CALL' },
            underlyingPriceForStatus
          );

          const unifiedPos: UnifiedPosition = {
            ...commonPositionProps,
            marketValue: calculatedMarketValue,
            assetTypeDisplay: 'Option',
            typeColumnDisplay: optionType,
            strikePrice: strikePrice,
            dteValue: dteValue,
            dte: typeof dteValue === 'number' ? dteValue : undefined,
            optionStatus: optionStatus,
            underlyingSymbol: underlyingSymbol,
            isOption: true,
          };
          processedData.allOptionPositions.push(unifiedPos);
          unifiedOptionPositionsCount++;
          if (typeof dteValue === 'number' && dteValue <= EXPIRING_SOON_DAYS) {
            unifiedExpiringSoonCount++;
          }
          if (optionStatus === 'ITM') {
            unifiedInTheMoneyCount++;
          }
        } else if (pos['instrument-type'] === 'Equity') {
          const stockSymbol = pos.symbol;
          const quoteInfo = quotes?.quotes?.[stockSymbol];
          const tastytradeMarkPrice = parseFloat(pos['mark-price']);
          let livePrice: number | undefined = undefined;

          if (quoteInfo && typeof quoteInfo.bp === 'number' && !isNaN(quoteInfo.bp)) {
            livePrice = quoteInfo.bp; // Use bid price (bp)
            console.log(`[PortfolioPage] Tasty Equity ${stockSymbol}: Alpaca quote bp: ${quoteInfo.bp}`);
          } else if (quoteInfo && typeof quoteInfo.ap === 'number' && typeof quoteInfo.bp === 'number' && !isNaN(quoteInfo.ap) && !isNaN(quoteInfo.bp)) {
            // Fallback to midpoint if bp specifically isn't good but ap/bp are available
            livePrice = (quoteInfo.ap + quoteInfo.bp) / 2;
            console.warn(`[PortfolioPage] Tasty Equity ${stockSymbol}: Alpaca quote bp was not a number (is ${quoteInfo.bp}). Fell back to midpoint: ${livePrice}`);
          }

          if (livePrice !== undefined && !isNaN(livePrice)) {
            calculatedMarketValue = livePrice; // Price per unit
            console.log(`[PortfolioPage] Tasty Equity ${stockSymbol}: Used Alpaca live price per unit: ${livePrice}. Quantity: ${quantity}.`);
          } else if (!isNaN(tastytradeMarkPrice)) {
            calculatedMarketValue = tastytradeMarkPrice; // Price per unit
            console.warn(`[PortfolioPage] Tasty Equity ${stockSymbol}: Alpaca quote unavailable/NaN (Quote: ${JSON.stringify(quoteInfo)}). Used Tastytrade mark price per unit: ${tastytradeMarkPrice}.`);
          } else {
            calculatedMarketValue = NaN;
            console.warn(`[PortfolioPage] Tasty Equity ${stockSymbol}: Both Alpaca and Tastytrade mark price are unavailable/NaN.`);
          }

          const unifiedPos: UnifiedPosition = {
            ...commonPositionProps,
            symbolDisplay: stockSymbol, // Ensure symbolDisplay is the stock symbol for equities
            marketValue: calculatedMarketValue,
            assetTypeDisplay: 'Equity',
            typeColumnDisplay: 'N/A',
            isOption: false,
          };
          processedData.allStockPositions.push(unifiedPos);
          unifiedStockPositionsCount++;
        } else { // Futures, Crypto, etc.
          const tastytradeMarkPrice = parseFloat(pos['mark-price']);
          const multiplier = pos.multiplier || 1;

          if (!isNaN(tastytradeMarkPrice)) {
            calculatedMarketValue = tastytradeMarkPrice * multiplier; // Price per unit (with multiplier)
            console.log(`[PortfolioPage] Tasty Other (${pos['instrument-type']}) ${pos.symbol}: Used mark price per unit: ${tastytradeMarkPrice} * multiplier ${multiplier} = ${calculatedMarketValue}. Quantity: ${quantity}.`);
          } else {
            calculatedMarketValue = NaN;
            console.warn(`[PortfolioPage] Tasty Other (${pos['instrument-type']}) ${pos.symbol}: mark-price is NaN.`);
          }
          
          const unifiedPos: UnifiedPosition = {
            ...commonPositionProps,
            symbolDisplay: pos.symbol, // Ensure symbolDisplay is the direct symbol
            marketValue: calculatedMarketValue,
            assetTypeDisplay: pos['instrument-type'] || 'Stock',
            typeColumnDisplay: 'N/A',
            isOption: false,
          };
          processedData.allStockPositions.push(unifiedPos);
          unifiedStockPositionsCount++;
        }
        unifiedAccountPositionsCount++;
      });

      processedData.accounts.push({
        broker: 'Tastytrade',
        accountNumber: accountNumber,
        value: accountValue,
        positionsCount: unifiedAccountPositionsCount,
        optionPositionsCount: unifiedOptionPositionsCount,
        stockPositionsCount: unifiedStockPositionsCount,
        expiringSoonCount: unifiedExpiringSoonCount,
        inTheMoneyCount: unifiedInTheMoneyCount,
        rawAccountData: { ...account, balanceData },
      });

      processedData.totalPortfolioValue += accountValue;
      processedData.totalPositionsCount += unifiedAccountPositionsCount;
      processedData.expiringSoonCount += unifiedExpiringSoonCount;
      processedData.inTheMoneyCount += unifiedInTheMoneyCount;
    });

    console.log('[PortfolioPage] processAndAdaptTastytradeData: Processing complete', processedData);
    return processedData;
  }, []);

  // Effect for processing and merging data
  useEffect(() => {
    // ADD THIS LOG:
    console.log('[PortfolioPage] Main processing effect - Before processing - loadingSources:', JSON.stringify(Array.from(loadingSources)), 'underlyingQuotes.quotes:', underlyingQuotes ? JSON.stringify(underlyingQuotes.quotes) : 'underlyingQuotes is null');
    console.log("[PortfolioPage] Main processing effect triggered. States:", { schwabConnected, tradierConnected, schwabSourceData, tradierSourceData, underlyingQuotes, loadingSources });

    if (loadingSources.has('Quotes')) {
        console.log("[PortfolioPage] Quotes are currently loading. Waiting for quotes to resolve...");
        return;
    }
    
    // Check if underlyingQuotes.quotes exists before proceeding if brokers are connected
    if ((schwabConnected || tradierConnected || tastytradeConnected) && underlyingQuotes && !underlyingQuotes.quotes && loadingSources.has('Quotes')) { // Added tastytradeConnected
        console.log("[PortfolioPage] Waiting for quotes to resolve (underlyingQuotes.quotes is missing and Quotes source is loading)...");
        return;
    }
     if ((schwabConnected || tradierConnected || tastytradeConnected) && underlyingQuotes && !underlyingQuotes.quotes && !loadingSources.has('Quotes')) { // Added tastytradeConnected
        console.warn("[PortfolioPage] Proceeding without quotes data as underlyingQuotes.quotes is missing and Quotes source is not loading. This might lead to N/A statuses.");
        // Potentially set underlyingQuotes to { quotes: {} } to prevent crashes if downstream code expects .quotes
        // However, the processing functions already handle quotes?.quotes?.
    }


    // If no brokers are connected at all, then clear the combined data.
    if (!schwabConnected && !tradierConnected && !tastytradeConnected) { // Added tastytradeConnected
      setCombinedPortfolioData(null);
      return;
    }
    
    // At this point:
    // - If any broker is connected, `underlyingQuotes` is not null (it's at least {}), OR quotes are not currently loading.
    // - We will proceed to build `finalCombinedData` using whatever sourceData is available for connected brokers.

    let finalCombinedData: CombinedPortfolioData = {
      totalPortfolioValue: 0,
      totalPositionsCount: 0,
      expiringSoonCount: 0,
      inTheMoneyCount: 0,
      accounts: [],
      allOptionPositions: [],
      allStockPositions: [],
      lastUpdated: new Date().toISOString(),
      sources: [],
    };

    // Log the structure of the quotes data for debugging
    console.log('[PortfolioPage] Final quotes data structure before processing:',
      underlyingQuotes ? 
        (underlyingQuotes.quotes ? 
          `quotes object present with ${Object.keys(underlyingQuotes.quotes).length} symbols` :
          'quotes property missing or empty'
        ) :
        'underlyingQuotes is null'
    );
    
    if (underlyingQuotes?.quotes) {
      console.log('[PortfolioPage] Available quote symbols:', Object.keys(underlyingQuotes.quotes));
    }

    if (schwabConnected && schwabSourceData && underlyingQuotes) { // Added condition
      console.log("[PortfolioPage] Processing Schwab data for combination...");
      const schwabProcessed = processAndAdaptSchwabData(schwabSourceData, underlyingQuotes);
      console.log("[PortfolioPage] Schwab data processing for combination complete.");
      finalCombinedData.totalPortfolioValue += schwabProcessed.totalPortfolioValue;
      finalCombinedData.totalPositionsCount += schwabProcessed.totalPositionsCount;
      finalCombinedData.expiringSoonCount += schwabProcessed.expiringSoonCount;
      finalCombinedData.inTheMoneyCount += schwabProcessed.inTheMoneyCount;
      finalCombinedData.accounts.push(...schwabProcessed.accounts);
      finalCombinedData.allOptionPositions.push(...schwabProcessed.allOptionPositions);
      finalCombinedData.allStockPositions.push(...schwabProcessed.allStockPositions);
      finalCombinedData.sources.push('Schwab');
    }

    if (tradierConnected && tradierSourceData && underlyingQuotes) {
      console.log("[PortfolioPage] Processing Tradier data for combination...");
      const tradierProcessed = processAndAdaptTradierData(tradierSourceData, underlyingQuotes);
      console.log("[PortfolioPage] Tradier data processing for combination complete.");
      finalCombinedData.totalPortfolioValue += tradierProcessed.totalPortfolioValue;
      finalCombinedData.totalPositionsCount += tradierProcessed.totalPositionsCount;
      finalCombinedData.expiringSoonCount += tradierProcessed.expiringSoonCount;
      finalCombinedData.inTheMoneyCount += tradierProcessed.inTheMoneyCount;
      finalCombinedData.accounts.push(...tradierProcessed.accounts);
      finalCombinedData.allOptionPositions.push(...tradierProcessed.allOptionPositions);
      finalCombinedData.allStockPositions.push(...tradierProcessed.allStockPositions);
      finalCombinedData.sources.push('Tradier');
    }

    if (tastytradeConnected && tastytradeSourceData && underlyingQuotes) { // Changed combinedPortfolioData to tastytradeSourceData
      console.log("[PortfolioPage] Processing Tastytrade data for combination...");
      const tastytradeProcessed = processAndAdaptTastytradeData(tastytradeSourceData, underlyingQuotes); // Changed combinedPortfolioData to tastytradeSourceData
      console.log("[PortfolioPage] Tastytrade data processing for combination complete.");
      finalCombinedData.totalPortfolioValue += tastytradeProcessed.totalPortfolioValue;
      finalCombinedData.totalPositionsCount += tastytradeProcessed.totalPositionsCount;
      finalCombinedData.expiringSoonCount += tastytradeProcessed.expiringSoonCount;
      finalCombinedData.inTheMoneyCount += tastytradeProcessed.inTheMoneyCount;
      finalCombinedData.accounts.push(...tastytradeProcessed.accounts);
      finalCombinedData.allOptionPositions.push(...tastytradeProcessed.allOptionPositions);
      finalCombinedData.allStockPositions.push(...tastytradeProcessed.allStockPositions);
      finalCombinedData.sources.push('Tastytrade');
    }
    
    // Recalculate total portfolio value from unified accounts for accuracy
    finalCombinedData.totalPortfolioValue = finalCombinedData.accounts.reduce((sum, acc) => sum + (acc.value || 0), 0);
    console.log(`[PortfolioPage] Final combined data ready to be set. Total Value: ${formatCurrency(finalCombinedData.totalPortfolioValue)}`);


    setCombinedPortfolioData(finalCombinedData);

  }, [
    schwabConnected, 
    tradierConnected, 
    tastytradeConnected, // Added tastytradeConnected
    schwabSourceData, 
    tradierSourceData, 
    tastytradeSourceData, // Added tastytradeSourceData
    underlyingQuotes, 
    processAndAdaptSchwabData, 
    processAndAdaptTradierData,
    processAndAdaptTastytradeData, // Added processAndAdaptTastytradeData
    loadingSources // Added loadingSources back
  ]);


  // Filtering logic for combined option positions
  const getFilteredPositions = useCallback(() => {
    if (!combinedPortfolioData) return [];
    
    let positions: UnifiedPosition[] = [];
    switch (activeTab) {
      case "expiring":
        positions = combinedPortfolioData.allOptionPositions.filter(p => {
          const dte = p.dteValue;
          return typeof dte === "number" && dte <= EXPIRING_SOON_DAYS;
        });
        break;
      case "itm":
        positions = combinedPortfolioData.allOptionPositions.filter(p => p.optionStatus === "ITM");
        break;
      case "all":
      default:
        positions = combinedPortfolioData.allOptionPositions;
        break;
    }
    
    return positions.filter(position => {
      if (selectedAccountId && position.accountId !== selectedAccountId) return false; // <-- Filter by selected account
      if (filterCriteria.symbol && (!position.symbolDisplay || !position.symbolDisplay.toLowerCase().includes(filterCriteria.symbol.toLowerCase()))) return false;
      if (filterCriteria.type && position.typeColumnDisplay !== filterCriteria.type) return false;
      const strikePrice = position.strikePrice;
      if (strikePrice !== undefined) {
        if (filterCriteria.minStrike !== undefined && strikePrice < filterCriteria.minStrike) return false;
        if (filterCriteria.maxStrike !== undefined && strikePrice > filterCriteria.maxStrike) return false;
      }
      const dte = position.dteValue;
      if (typeof dte === 'number') {
        if (filterCriteria.minDTE !== undefined && dte < filterCriteria.minDTE) return false;
        if (filterCriteria.maxDTE !== undefined && dte > filterCriteria.maxDTE) return false;
      }
      if (filterCriteria.status && position.optionStatus !== filterCriteria.status) return false;
      if (filterCriteria.broker && position.broker !== filterCriteria.broker) return false; // Added broker filter check
      return true;
    });
  }, [combinedPortfolioData, activeTab, filterCriteria, selectedAccountId]); // Corrected this line

  // Filter function for combined stock positions
  const getFilteredStockPositions = useCallback(() => {
    if (!combinedPortfolioData) return [];
    return combinedPortfolioData.allStockPositions.filter(position => {
      if (selectedAccountId && position.accountId !== selectedAccountId) return false; // <-- Filter by selected account
      if (stockFilterCriteria.symbol && (!position.symbolDisplay || !position.symbolDisplay.toLowerCase().includes(stockFilterCriteria.symbol.toLowerCase()))) return false;
      if (stockFilterCriteria.broker && position.broker !== stockFilterCriteria.broker) return false; // Added broker filter check for stocks
      return true;
    });
  }, [combinedPortfolioData, stockFilterCriteria, selectedAccountId]); // <-- Added selectedAccountId to dependencies

  useEffect(() => {
    const newActiveFilters: string[] = [];
    if (filterCriteria.symbol) newActiveFilters.push(`Symbol: ${filterCriteria.symbol}`);
    if (filterCriteria.type) newActiveFilters.push(`Type: ${filterCriteria.type}`);
    if (filterCriteria.minStrike !== undefined || filterCriteria.maxStrike !== undefined) {
      newActiveFilters.push(`Strike: ${filterCriteria.minStrike !== undefined ? `$${filterCriteria.minStrike}` : '$0'} - ${filterCriteria.maxStrike !== undefined ? `$${filterCriteria.maxStrike}` : '∞'}`);
    }
    if (filterCriteria.minDTE !== undefined || filterCriteria.maxDTE !== undefined) {
      newActiveFilters.push(`DTE: ${filterCriteria.minDTE !== undefined ? filterCriteria.minDTE : '0'} - ${filterCriteria.maxDTE !== undefined ? filterCriteria.maxDTE : '∞'}`);
    }
    if (filterCriteria.status) newActiveFilters.push(`Status: ${filterCriteria.status}`);
    if (filterCriteria.broker) newActiveFilters.push(`Broker: ${filterCriteria.broker}`); // Added broker to active filters display
    setActiveFilters(newActiveFilters);
  }, [filterCriteria]);

  const handleFilterChange = (field: keyof FilterCriteria, value: any) => {
    let processedValue = value;
    // For broker, if "All" is selected (value is empty string), set to undefined
    if (field === 'broker' && value === '') {
        processedValue = undefined;
    }
    // For numeric fields, ensure they are numbers or undefined
    if (['minStrike', 'maxStrike', 'minDTE', 'maxDTE'].includes(field)) {
        processedValue = value === '' ? undefined : parseFloat(value);
        if (isNaN(processedValue as number)) {
            processedValue = undefined;
        }
    }
    setFilterCriteria(prev => ({ ...prev, [field]: processedValue }));
  };
  
  const clearAllFilters = () => setFilterCriteria({});
  
  const removeFilter = (filterIndex: number) => {
    const filter = activeFilters[filterIndex];
    if (!filter) return;
    if (filter.startsWith('Symbol:')) setFilterCriteria(prev => ({ ...prev, symbol: undefined }));
    else if (filter.startsWith('Type:')) setFilterCriteria(prev => ({ ...prev, type: undefined }));
    else if (filter.startsWith('Strike:')) setFilterCriteria(prev => ({ ...prev, minStrike: undefined, maxStrike: undefined }));
    else if (filter.startsWith('DTE:')) setFilterCriteria(prev => ({ ...prev, minDTE: undefined, maxDTE: undefined }));
    else if (filter.startsWith('Status:')) setFilterCriteria(prev => ({ ...prev, status: undefined }));
    else if (filter.startsWith('Broker:')) setFilterCriteria(prev => ({ ...prev, broker: undefined })); // Added broker filter removal
  };

  useEffect(() => {
    const newActiveFilters: string[] = [];
    if (stockFilterCriteria.symbol) newActiveFilters.push(`Symbol: ${stockFilterCriteria.symbol}`);
    if (stockFilterCriteria.broker) newActiveFilters.push(`Broker: ${stockFilterCriteria.broker}`); // Added broker to active stock filters display
    setActiveStockFilters(newActiveFilters);
  }, [stockFilterCriteria]);

  const handleStockFilterChange = (field: keyof typeof stockFilterCriteria, value: any) => {
    // For broker, if "All" is selected (value is empty string), set to undefined
    let processedValue = value;
    if (field === 'broker' && value === '') {
        processedValue = undefined;
    }
    setStockFilterCriteria(prev => ({ ...prev, [field]: processedValue === '' ? undefined : processedValue }));
  };
  
  const clearAllStockFilters = () => setStockFilterCriteria({});
  
  const removeStockFilter = (filterIndex: number) => {
    const filter = activeStockFilters[filterIndex];
    if (filter?.startsWith('Symbol:')) setStockFilterCriteria(prev => ({ ...prev, symbol: undefined }));
    else if (filter?.startsWith('Broker:')) setStockFilterCriteria(prev => ({ ...prev, broker: undefined })); // Added broker filter removal for stocks
  };

  const sortedPositions = useMemo(() => {
    let sortableItems = getFilteredPositions(); // Initialize sortableItems

    const getSortValue = (item: UnifiedPosition, key: SortableKey): string | number | undefined => {
      switch (key) {
        case 'symbolDisplay': return item.symbolDisplay;
        case 'assetTypeDisplay': return item.assetTypeDisplay;
        case 'typeColumnDisplay': return item.typeColumnDisplay || 'N/A';
        case 'strikePrice': return item.strikePrice;
        case 'dte': return item.dteValue;
        case 'quantity': return item.quantity;
        case 'averagePrice': return item.averagePrice;
        case 'marketValue': return item.marketValue;
        case 'status': return item.optionStatus || '';
        case 'broker': return item.broker;
        default: return '';
      }
    };

    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;
        
        // sortConfig.key is guaranteed to be non-null here by the outer if condition
        aValue = getSortValue(a, sortConfig.key as SortableKey); 
        bValue = getSortValue(b, sortConfig.key as SortableKey);

        // Corrected generic comparison logic:
        if (aValue === bValue) { // Handles identical values, including both null/undefined
          return 0;
        }
        // Handle nulls/undefined to sort them consistently (e.g., at the end)
        if (aValue == null) { // a is null/undefined, b is not
          return sortConfig.direction === 'ascending' ? 1 : -1; 
        }
        if (bValue == null) { // b is null/undefined, a is not
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }

        // Type-specific comparisons
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          const diff = aValue - bValue;
          return sortConfig.direction === 'ascending' ? diff : -diff;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortConfig.direction === 'ascending' ? comparison : -comparison;
        }
        
        // Fallback for other types or mixed types (e.g., booleans)
        // Convert to string for a basic, consistent comparison
        const strA = String(aValue);
        const strB = String(bValue);
        const strComparison = strA.localeCompare(strB);
        if (strComparison !== 0) {
            return sortConfig.direction === 'ascending' ? strComparison : -strComparison;
        }
        
        return 0; // If all else fails or values are deemed equal by conversion
      });
    }
    return sortableItems;
  }, [getFilteredPositions, sortConfig]);

  const requestSort = (key: SortableKey) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (columnKey: SortableKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'ascending' ? <ChevronUp className="inline ml-1 h-4 w-4" /> : <ChevronDown className="inline ml-1 h-4 w-4" />;
  };
  
  const sortedStockPositions = useMemo(() => {
    let sortableItems = [...getFilteredStockPositions()];
    if (stockSortConfig.key !== null) {
      const getStockSortValue = (item: UnifiedPosition, key: SortableKey): any => {
        switch (key) {
          case 'symbolDisplay': return item.symbolDisplay;
          case 'quantity': return item.quantity;
          case 'averagePrice': return item.averagePrice;
          case 'marketValue': return item.marketValue;
          case 'broker': return item.broker;
          default:
            // For keys not relevant to stocks or unexpected keys, return null.
            return null; 
        }
      };

      sortableItems.sort((a, b) => {
        const aValue = getStockSortValue(a, stockSortConfig.key!);
        const bValue = getStockSortValue(b, stockSortConfig.key!);

        if (aValue === bValue) {
          return 0;
        }
        if (aValue == null) { // a is null/undefined, b is not
          return stockSortConfig.direction === 'ascending' ? 1 : -1; 
        }
        if (bValue == null) { // b is null/undefined, a is not
          return stockSortConfig.direction === 'ascending' ? -1 : 1;
        }

        // Type-specific comparisons
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          const diff = aValue - bValue;
          return stockSortConfig.direction === 'ascending' ? diff : -diff;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return stockSortConfig.direction === 'ascending' ? comparison : -comparison;
        }
        
        // Fallback for other types or mixed types
        const strA = String(aValue);
        const strB = String(bValue);
        const strComparison = strA.localeCompare(strB);
        if (strComparison !== 0) {
            return stockSortConfig.direction === 'ascending' ? strComparison : -strComparison;
        }
        
        return 0; // If all else fails or values are deemed equal
      });
    }
    return sortableItems;
  }, [getFilteredStockPositions, stockSortConfig]);

  const requestStockSort = (key: SortableKey) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (stockSortConfig.key === key && stockSortConfig.direction === 'ascending') direction = 'descending';
    setStockSortConfig({ key, direction });
  };

  const renderStockSortIcon = (columnKey: SortableKey) => {
    if (stockSortConfig.key !== columnKey) return null;
    return stockSortConfig.direction === 'ascending' ? <ChevronUp className="inline ml-1 h-4 w-4" /> : <ChevronDown className="inline ml-1 h-4 w-4" />;
  };

  const displayedPositions = sortedPositions;
  const displayedStockPositions = sortedStockPositions;

  // Memoize the callback for Tastytrade connection change
  const handleTastytradeConnectionChange = useCallback((status: boolean) => {
    setBrokerConnected('tastytradeConnected', status);
  }, [setBrokerConnected]);

  // UI Rendering
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Portfolio Overview</h1>

        <BrokerConnectionManager

         

          schwabConnected={schwabConnected}
          onSchwabConnectionChange={setSchwabConnected}
          tradierConnected={tradierConnected}
          onTradierConnectionChange={setTradierConnected}
          tastytradeConnected={tastytradeConnected}
          onTastytradeConnectionChange={handleTastytradeConnectionChange} // Use memoized callback
          loadingSources={loadingSources} // Pass loadingSources
          errorSources={errorSources}     // Pass errorSources
        />

        {/* Loading and Error Indicators */}
               {Object.values(errorSources).some(e => e) && (
          <div className="text-center py-4 text-red-600">
            {Object.entries(errorSources).map(([source, error]) => 
              error ? <div key={source}>Error ({source}): {error}</div> : null
            )}
          </div>
        )}

        {/* Content or Connect/No Data Message Area */}
        {/* Show content if not loading critical parts, no major errors, at least one broker connected, and data is available */}
        {loadingSources.size === 0 && !Object.values(errorSources).some(e => e) && (schwabConnected || tradierConnected || tastytradeConnected) && combinedPortfolioData && (
          <>
            {/* Account Summary Card */}
            <div className="bg-white shadow-lg rounded-lg p-6 mb-8">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">Account Summary</h2>
                  <p className="text-sm text-gray-500">Last updated: {new Date(combinedPortfolioData.lastUpdated).toLocaleString()}</p>
                </div>
                <div className="flex space-x-2">
                  {combinedPortfolioData.sources.includes('Schwab') && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <svg className="mr-1.5 h-2 w-2 text-blue-400" fill="currentColor" viewBox="0 0 8 8"><circle cx={4} cy={4} r={3} /></svg>
                      Charles Schwab
                    </span>
                  )}
                  {combinedPortfolioData.sources.includes('Tradier') && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <svg className="mr-1.5 h-2 w-2 text-green-400" fill="currentColor" viewBox="0 0 8 8"><circle cx={4} cy={4} r={3} /></svg>
                      Tradier
                    </span>
                  )}
                  {combinedPortfolioData.sources.includes('Tastytrade') && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      <svg className="mr-1.5 h-2 w-2 text-purple-400" fill="currentColor" viewBox="0 0 8 8"><circle cx={4} cy={4} r={3} /></svg>
                      Tastytrade
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-center">
                <div>
                  <p className="text-sm text-gray-500">Total Portfolio Value</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(combinedPortfolioData.totalPortfolioValue)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Positions</p>
                  <p className="text-2xl font-bold text-gray-900">{combinedPortfolioData.totalPositionsCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Accounts</p>
                  <p className="text-2xl font-bold text-gray-900">{combinedPortfolioData.accounts.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expiring Soon (Options)</p>
                  <p className="text-2xl font-bold text-gray-900">{combinedPortfolioData.expiringSoonCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">In The Money (Options)</p>
                  <p className="text-2xl font-bold text-gray-900">{combinedPortfolioData.inTheMoneyCount}</p>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Accounts</h3>
                {combinedPortfolioData.accounts.length > 0 ? (
                  <div className="space-y-4">
                    {combinedPortfolioData.accounts.map((account) => (
                      <div 
                        key={`${account.broker}-${account.accountNumber}`}
                        className={`bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer ${selectedAccountId === account.accountNumber ? 'ring-2 ring-blue-500' : 'ring-1 ring-gray-200 dark:ring-gray-700'}`}
                        onClick={() => setSelectedAccountId(prevId => prevId === account.accountNumber ? null : account.accountNumber)} // <-- Set/unset selected account
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">
                            {account.broker} Acc: ...{account.accountNumber.slice(-4)}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${account.broker === 'Schwab' ? 'bg-blue-100 text-blue-700' : account.broker === 'Tradier' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                            {account.broker}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Value</p>
                            <p className="text-gray-800 dark:text-gray-200 font-medium">{formatCurrency(account.value)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Positions</p>
                            <p className="text-gray-800 dark:text-gray-200 font-medium">{account.positionsCount}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">Expiring Soon</p>
                            <p className="text-gray-800 dark:text-gray-200 font-medium">{account.expiringSoonCount}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 dark:text-gray-400">In The Money</p>
                            <p className="text-gray-800 dark:text-gray-200 font-medium">{account.inTheMoneyCount}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No accounts to display.</p>
                )}
              </div>
            </div>

            {/* Option Positions Table */}
            <Collapsible defaultOpen={true} className="bg-white shadow-lg rounded-lg p-6 mb-8">
              <CollapsibleTrigger className="w-full">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800 mb-1">Option Positions</h2>
                  {/* Add a chevron or similar icon to indicate collapsible state if desired */}
                  <ChevronDown className="h-5 w-5 collapsible-indicator" /> {/* Example icon, adjust as needed */}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                {combinedPortfolioData.allOptionPositions.length > 0 ? (
                  <>
                    <p className="text-sm text-gray-500 mb-4">View and analyze your option positions</p>
                    
                    <div className="mb-4 border-b border-gray-200">
                      <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        {['all', 'expiring', 'itm'].map((tab) => (
                          <button
                            key={tab}
                            onClick={() => setActiveTab(tab as 'all' | 'expiring' | 'itm')}
                            className={`${
                              activeTab === tab
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm`}
                          >
                            {tab === 'all' ? 'All Options' : tab === 'expiring' ? `Expiring (${EXPIRING_SOON_DAYS} Days)` : 'In The Money Options'}
                          </button>
                        ))}
                      </nav>
                    </div>

                    {/* Filter controls */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <button
                          onClick={() => setShowFilterPanel(!showFilterPanel)}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {showFilterPanel ? 'Hide Filters' : 'Show Filters'}
                        </button>
                        
                        <div className="text-sm text-gray-500">
                          {displayedPositions.length} position{displayedPositions.length !== 1 ? 's' : ''} found
                        </div>
                      </div>

                      {/* Active filters display */}
                      {activeFilters.length > 0 && (
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <span className="text-sm text-gray-500">Active filters:</span>
                          {activeFilters.map((filter, index) => (
                            <span 
                              key={filter}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {filter}
                              <button
                                type="button"
                                onClick={() => removeFilter(index)}
                                className="ml-1 flex-shrink-0 inline-flex text-blue-500 hover:text-blue-700"
                              >
                                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </span>
                          ))}
                          {activeFilters.length > 0 && (
                            <button
                              onClick={clearAllFilters}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              Clear all
                            </button>
                          )}
                        </div>
                      )}

                      {/* Filter panel */}
                      {showFilterPanel && (
                        <div className="bg-gray-50 p-4 rounded-md mb-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4"> {/* Adjusted grid for new filter */}
                            <div>
                              <label htmlFor="symbol-filter" className="block text-sm font-medium text-gray-700">Symbol</label>
                              <input
                                type="text"
                                id="symbol-filter"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                placeholder="E.g., AAPL"
                                value={filterCriteria.symbol || ''}
                                onChange={(e) => handleFilterChange('symbol', e.target.value)}
                                aria-label="Filter by option symbol"
                              />
                            </div>
                            <div>
                              <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700">Option Type</label>
                              <select
                                id="type-filter"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                value={filterCriteria.type || ''}
                                onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                              >
                                <option value="">All</option>
                                <option value="PUT">PUT</option>
                                <option value="CALL">CALL</option>
                              </select>
                            </div>
                            <div>
                              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700">Status</label>
                              <select
                                id="status-filter"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                value={filterCriteria.status || ''}
                                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                              >
                                <option value="">All</option>
                                <option value="ITM">ITM</option>
                                <option value="OTM">OTM</option>
                              </select>
                            </div>
                            <div>
                              <label htmlFor="broker-filter" className="block text-sm font-medium text-gray-700">Broker</label>
                              <select
                                id="broker-filter"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                value={filterCriteria.broker || ''}
                                onChange={(e) => handleFilterChange('broker', e.target.value as 'Schwab' | 'Tradier' | '')}
                              >
                                <option value="">All Brokers</option>
                                <option value="Schwab">Schwab</option>
                                <option value="Tradier">Tradier</option>
                                <option value="Tastytrade">Tastytrade</option> // Added Tastytrade to filter
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Strike Price Range</label>
                              <div className="flex space-x-2 mt-1">
                                <input
                                  type="number"
                                  className="block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                  placeholder="Min"
                                  value={filterCriteria.minStrike ?? ''}
                                  onChange={(e) => handleFilterChange('minStrike', e.target.value)}
                                  aria-label="Minimum strike price filter"
                                />
                                <span className="flex items-center text-gray-500">to</span>
                                <input
                                  type="number"
                                  className="block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                  placeholder="Max"
                                  value={filterCriteria.maxStrike ?? ''}
                                  onChange={(e) => handleFilterChange('maxStrike', e.target.value)}
                                  aria-label="Maximum strike price filter"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">DTE Range</label>
                              <div className="flex space-x-2 mt-1">
                                <input
                                  type="number"
                                  className="block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                  placeholder="Min"
                                  value={filterCriteria.minDTE ?? ''}
                                  onChange={(e) => handleFilterChange('minDTE', e.target.value)}
                                  aria-label="Minimum days to expiration filter"
                                />
                                <span className="flex items-center text-gray-500">to</span>
                                <input
                                  type="number"
                                  className="block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                  placeholder="Max"
                                  value={filterCriteria.maxDTE ?? ''}
                                  onChange={(e) => handleFilterChange('maxDTE', e.target.value)}
                                  aria-label="Maximum days to expiration filter"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('symbolDisplay')}>Symbol {renderSortIcon('symbolDisplay')}</th>
                            {/* Asset Type might be redundant if this table is only options */}
                            {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('assetTypeDisplay')}>Asset Type {renderSortIcon('assetTypeDisplay')}</th> */}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('typeColumnDisplay')}>Type {renderSortIcon('typeColumnDisplay')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('strikePrice')}>Strike {renderSortIcon('strikePrice')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('dte')}>DTE {renderSortIcon('dte')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('quantity')}>Quantity {renderSortIcon('quantity')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestSort('status')}>Status {renderSortIcon('status')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayedPositions.length > 0 ? displayedPositions.map((pos, index) => {
                            const statusBadgeColor = pos.optionStatus === "ITM" ? "bg-green-100 text-green-800" : pos.optionStatus === "OTM" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800";
                            return (
                              <tr key={`${pos.broker}-${pos.positionId || index}-${pos.symbolDisplay}-${pos.strikePrice}-${pos.typeColumnDisplay}`}> 
                                {/* <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{pos.broker}</td> */}
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{pos.symbolDisplay}</td>
                                {/* <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{pos.assetTypeDisplay}</td> */}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{pos.typeColumnDisplay || 'N/A'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{pos.strikePrice !== undefined ? formatCurrency(pos.strikePrice) : 'N/A'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{pos.dteValue}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{pos.quantity}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadgeColor}`}>{pos.optionStatus || 'N/A'}</span>
                                </td>
                              </tr>
                            );
                          }) : (
                            <tr><td colSpan={6} className="px-4 py-4 text-center text-sm text-gray-500">No option positions to display for the selected filters.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-gray-500 py-4">No option positions to display.</p>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Stock Positions Table */}
            <Collapsible defaultOpen={true} className="bg-white shadow-lg rounded-lg p-6 mb-8">
              <CollapsibleTrigger className="w-full">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800 mb-1">Stock Positions</h2>
                  <ChevronDown className="h-5 w-5 collapsible-indicator" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                {combinedPortfolioData.allStockPositions.length > 0 ? (
                  <>
                    <p className="text-sm text-gray-500 mb-4">Overview of your stock holdings</p>
                    
                    {/* Filter controls for stocks */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <button
                          onClick={() => setShowStockFilterPanel(!showStockFilterPanel)}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {showStockFilterPanel ? 'Hide Filters' : 'Show Filters'}
                        </button>
                        
                        <div className="text-sm text-gray-500">
                          {displayedStockPositions.length} position{displayedStockPositions.length !== 1 ? 's' : ''} found
                        </div>
                      </div>

                      {/* Active stock filters display */}
                      {activeStockFilters.length > 0 && (
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <span className="text-sm text-gray-500">Active filters:</span>
                          {activeStockFilters.map((filter, index) => (
                            <span 
                              key={filter}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {filter}
                              <button
                                type="button"
                                onClick={() => removeStockFilter(index)}
                                className="ml-1 flex-shrink-0 inline-flex text-blue-500 hover:text-blue-700"
                              >
                                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </span>
                          ))}
                          {activeStockFilters.length > 0 && (
                            <button
                              onClick={clearAllStockFilters}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              Clear all
                            </button>
                          )}
                        </div>
                      )}

                      {/* Filter panel for stocks */}
                      {showStockFilterPanel && (
                        <div className="bg-gray-50 p-4 rounded-md mb-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> {/* Adjusted grid for new filter */}
                            <div>
                              <label htmlFor="stock-symbol-filter" className="block text-sm font-medium text-gray-700">Symbol</label>
                              <input
                                type="text"
                                id="stock-symbol-filter"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                placeholder="E.g., AAPL"
                                value={stockFilterCriteria.symbol || ''}
                                onChange={(e) => handleStockFilterChange('symbol', e.target.value)}
                                aria-label="Filter by stock symbol"
                              />
                            </div>
                            <div>
                              <label htmlFor="stock-broker-filter" className="block text-sm font-medium text-gray-700">Broker</label>
                              <select
                                id="stock-broker-filter"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                                value={stockFilterCriteria.broker || ''}
                                onChange={(e) => handleStockFilterChange('broker', e.target.value as 'Schwab' | 'Tradier' | '')}
                              >
                                <option value="">All Brokers</option>
                                <option value="Schwab">Schwab</option>
                                <option value="Tradier">Tradier</option>
                                <option value="Tastytrade">Tastytrade</option> // Added Tastytrade to filter
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestStockSort('broker')}>Broker {renderStockSortIcon('broker')}</th> */}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestStockSort('symbolDisplay')}>Symbol {renderStockSortIcon('symbolDisplay')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestStockSort('quantity')}>Quantity {renderStockSortIcon('quantity')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestStockSort('averagePrice')}>Avg Price {renderStockSortIcon('averagePrice')}</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => requestStockSort('marketValue')}>Market Value {renderStockSortIcon('marketValue')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayedStockPositions.length > 0 ? displayedStockPositions.map((stock, index) => (
                            <tr key={`${stock.broker}-${stock.positionId || index}-${stock.symbolDisplay}`}>
                              {/* <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{stock.broker}</td> */}
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{stock.symbolDisplay}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{stock.quantity}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatCurrency(stock.averagePrice)}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatCurrency(stock.marketValue)}</td>
                            </tr>
                          )) : (
                            <tr><td colSpan={4} className="px-4 py-4 text-center text-sm text-gray-500">No stock positions to display for the selected filter.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-gray-500 py-4">No stock positions to display.</p>
                )}
              </CollapsibleContent>
            </Collapsible>
          </>
        )}

        {/* "Connect to broker" or "No data" message - Show if not loading, no errors, but conditions for content are not met */}
        {loadingSources.size === 0 && !Object.values(errorSources).some(e => e) && (!combinedPortfolioData || !(schwabConnected || tradierConnected || tastytradeConnected)) && ( // Added tastytradeConnected
          <div className="text-center py-10">
            <p className="text-gray-600">
              {!(schwabConnected || tradierConnected || tastytradeConnected) // Added tastytradeConnected
                ? "Connect to a broker to view your portfolio." 
                : "No portfolio data to display. Ensure connections are active and data has loaded, or check for minor loading issues."}
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default PortfolioPage;

// Basic styling for the collapsible indicator (optional)
const styles = `
  .collapsible-indicator[data-state='open'] {
    transform: rotate(180deg);
  }
  .collapsible-indicator {
    transition: transform 0.2s ease-out;
  }
`;

if (typeof window !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}
